import React, { useState, useEffect, useRef } from 'react';
import { db, auth } from './firebase';
import { collection, onSnapshot, query, orderBy, doc, setDoc, deleteDoc, getDoc, addDoc, getDocs, where } from 'firebase/firestore';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut, updatePassword } from 'firebase/auth';
import {
  Shield, MapPin, AlertTriangle, LogOut, Loader2,
  Users, Building2, Settings, Plus, Trash2, Edit, Printer, CheckCircle2,
  LayoutDashboard, MapPinned, FileSearch, ShieldCheck, Landmark, KeyRound, SlidersHorizontal,
  Activity, Zap, Award, Infinity as InfinityIcon, Search, X, TrendingUp, Calendar, Clock, FileText, Download, Filter,
  Battery, History, Volume2, AlertCircle, ChevronRight, BarChart3, Globe, Smartphone, FileCheck, Copy, Check, Lock, BellRing, Navigation, ScanLine, Laptop, Palette, Mail
} from 'lucide-react';
import { format, addYears, isAfter, isSameDay, addMonths, differenceInDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { QRCodeCanvas } from 'qrcode.react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Cell, PieChart, Pie } from 'recharts';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// --- UI HELPERS ---

function NavItem({ active, icon, label, onClick }) {
  return (
    <button onClick={onClick} className={`flex items-center gap-4 w-full p-4 rounded-2xl transition-all font-black text-sm uppercase tracking-tighter ${active ? 'bg-blue-600 text-white shadow-xl shadow-blue-600/20 translate-x-2' : 'text-slate-500 hover:bg-slate-800 hover:text-white'}`}>
      {React.cloneElement(icon, { size: 20 })} <span>{label}</span>
    </button>
  );
}

function SectionHeader({ title, subtitle, icon }) {
  return (
    <div className="flex items-center gap-6 mb-12 animate-in fade-in slide-in-from-left duration-500">
      <div className="p-5 bg-gradient-to-br from-blue-600 to-blue-800 text-white rounded-[1.75rem] shadow-2xl shadow-blue-900/20">{icon}</div>
      <div>
        <h2 className="text-4xl font-black text-slate-800 tracking-tighter uppercase leading-none">{title}</h2>
        <div className="flex items-center gap-3 mt-2"><div className="h-[2px] w-8 bg-blue-600"></div><p className="text-slate-400 font-black text-[10px] uppercase tracking-[4px]">{subtitle}</p></div>
      </div>
    </div>
  );
}

function StatCard({ label, value, color, icon, trend }) {
  const gradients = {
    blue: 'from-blue-600 to-indigo-700 shadow-blue-900/20',
    green: 'from-emerald-500 to-teal-700 shadow-emerald-900/20',
    purple: 'from-violet-600 to-purple-800 shadow-purple-900/20',
    red: 'from-rose-500 to-red-700 shadow-red-900/20'
  };
  return (
    <div className="bg-white p-8 rounded-[2.5rem] border border-slate-50 shadow-sm relative overflow-hidden group hover:shadow-2xl hover:-translate-y-1 transition-all duration-500">
      <div className="flex justify-between items-start mb-8 z-10 relative">
        <div className={`bg-gradient-to-br ${gradients[color]} text-white p-4 rounded-[1.25rem] shadow-2xl group-hover:scale-110 transition-transform duration-500`}>{icon}</div>
        <span className="text-[10px] font-black text-slate-400 bg-slate-50 px-3 py-1.5 rounded-full uppercase border border-slate-100 tracking-widest">{trend}</span>
      </div>
      <div className="z-10 relative">
        <p className="text-5xl font-black text-slate-800 tracking-tighter mb-1 leading-none">{value}</p>
        <p className="text-[11px] font-black text-slate-400 uppercase tracking-[3px] mt-2 opacity-70">{label}</p>
      </div>
      <div className="absolute -right-8 -bottom-8 opacity-[0.03] text-slate-900 rotate-12 scale-[2.5] group-hover:rotate-0 transition-all duration-1000">{icon}</div>
    </div>
  );
}

function LicenseBadge({ expiryDate, type }) {
  if (type === 'LIFE' || !expiryDate) return <span className="flex items-center gap-2 text-[10px] font-black text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded-xl uppercase tracking-tighter border border-emerald-100"><InfinityIcon size={12}/> INFINITY</span>;
  const isExpired = !isAfter(new Date(expiryDate), new Date());
  return <span className={`flex items-center gap-2 text-[10px] font-black px-3 py-1.5 rounded-xl uppercase tracking-tighter border ${isExpired ? 'bg-red-50 text-red-600 border-red-100' : 'bg-orange-50 text-orange-600 border-orange-100'}`}><Award size={12}/> {isExpired ? 'EXPIRÉ' : format(new Date(expiryDate), 'dd/MM/yyyy')}</span>;
}

function InputField({ label, value, onChange, type = "text", placeholder = "" }) {
  return (
    <div className="space-y-2">
      <label className="text-[10px] font-black text-slate-400 uppercase tracking-[2px] ml-2 block">{label}</label>
      <input type={type} placeholder={placeholder} required className="w-full p-5 bg-slate-50 border-2 border-transparent rounded-[1.5rem] outline-none focus:border-blue-600 focus:bg-white font-bold text-slate-800 transition-all shadow-inner" value={value} onChange={e => onChange(e.target.value)} />
    </div>
  );
}

function PermissionToggle({ label, checked, onChange }) {
  return (
    <label className="flex items-center justify-between p-5 bg-white rounded-2xl cursor-pointer hover:bg-blue-50 transition-all border-2 border-slate-50 shadow-sm">
      <span className="text-xs font-black text-slate-600 uppercase tracking-tighter">{label}</span>
      <input type="checkbox" className="w-6 h-6 accent-blue-600" checked={checked} onChange={e => onChange(e.target.checked)} />
    </label>
  );
}

function DetailCard({ icon, label, value }) {
  return (
    <div className="bg-slate-50 p-6 rounded-3xl border-2 border-slate-100 shadow-sm transition-all hover:bg-white hover:shadow-lg">
      <div className="flex items-center gap-3 text-slate-400 mb-2 uppercase tracking-[2px] text-[10px] font-black">{icon} <span>{label}</span></div>
      <p className="font-black text-slate-800 truncate text-lg tracking-tighter">{value}</p>
    </div>
  );
}

function MenuSection({ title, children }) {
  return (
    <div className="space-y-4">
      <p className="text-[10px] font-black text-slate-600 uppercase tracking-[6px] px-4 opacity-50">{title}</p>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

// --- VIEW COMPONENTS ---

function DashboardView({ patrols, agents, sites, incidents, onNavigate }) {
  const last7Days = Array.from({length: 7}, (_, i) => { const d = new Date(); d.setDate(d.getDate() - (6 - i)); return d; });
  const chartData = last7Days.map(day => ({
    name: format(day, 'EEE', { locale: fr }),
    rondes: patrols.filter(p => isSameDay(new Date(p.endTime), day)).length,
    incidents: incidents.filter(i => isSameDay(new Date(i.timestamp), day)).length
  }));

  const totalPoints = patrols.reduce((acc, p) => acc + (p.pointsTotal || 0), 0);
  const scannedPoints = patrols.reduce((acc, p) => acc + (p.pointsScanned || 0), 0);
  const globalPerformance = totalPoints > 0 ? Math.round((scannedPoints / totalPoints) * 100) : 0;

  const incidentTypes = incidents.reduce((acc, inc) => {
    acc[inc.type] = (acc[inc.type] || 0) + 1;
    return acc;
  }, {});

  const incidentData = Object.keys(incidentTypes).map(key => ({ name: key, value: incidentTypes[key] }));
  const COLORS = ['#2563eb', '#ef4444', '#f59e0b', '#10b981', '#6366f1'];

  return (
    <div className="space-y-10 animate-in fade-in slide-in-from-bottom-6 duration-700 font-sans">
      {/* Top Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard label="Agents Terrain" value={agents.length} color="blue" icon={<ShieldCheck size={28}/>} trend={`${agents.filter(a => a.lastActive && (Date.now() - a.lastActive < 300000)).length} Actifs`} />
        <StatCard label="Taux d'Exécution" value={`${globalPerformance}%`} color="green" icon={<CheckCircle2 size={28}/>} trend="Performance" />
        <StatCard label="Missions (7j)" value={patrols.length} color="purple" icon={<Activity size={28}/>} trend="Volume" />
        <StatCard label="Incidents" value={incidents.length} color="red" icon={<AlertTriangle size={28}/>} trend="Signalés" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
        {/* Main Chart */}
        <div className="xl:col-span-2 bg-white rounded-[3rem] border border-slate-100 p-10 shadow-sm">
          <div className="flex justify-between items-center mb-10">
            <div>
              <h4 className="font-black text-slate-800 text-2xl tracking-tighter uppercase leading-none">Activité des Patrouilles</h4>
              <p className="text-slate-400 text-xs font-bold uppercase tracking-widest mt-2">Comparaison missions vs incidents</p>
            </div>
            <div className="flex gap-4">
              <div className="flex items-center gap-2"><div className="w-3 h-3 bg-blue-600 rounded-full"></div><span className="text-[10px] font-black uppercase text-slate-400">Rondes</span></div>
              <div className="flex items-center gap-2"><div className="w-3 h-3 bg-red-500 rounded-full"></div><span className="text-[10px] font-black uppercase text-slate-400">Alertes</span></div>
            </div>
          </div>
          <div className="h-[400px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="colorR" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2563eb" stopOpacity={0.1}/>
                    <stop offset="95%" stopColor="#2563eb" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f8fafc" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fill: '#94a3b8', fontSize: 12, fontWeight: '900'}} dy={15} />
                <YAxis hide />
                <Tooltip
                  contentStyle={{ borderRadius: '20px', border: 'none', boxShadow: '0 20px 40px -10px rgba(0,0,0,0.1)', padding: '15px' }}
                  itemStyle={{ fontWeight: '900', textTransform: 'uppercase', fontSize: '10px' }}
                />
                <Area type="monotone" dataKey="rondes" stroke="#2563eb" strokeWidth={4} fillOpacity={1} fill="url(#colorR)" />
                <Area type="monotone" dataKey="incidents" stroke="#ef4444" strokeWidth={3} fillOpacity={0} strokeDasharray="5 5" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Incident Breakdown */}
        <div className="bg-white rounded-[3rem] border border-slate-100 p-10 shadow-sm flex flex-col">
          <h4 className="font-black text-slate-800 text-xl tracking-tighter uppercase mb-8">Typologie d'Alertes</h4>
          <div className="flex-1 min-h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={incidentData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {incidentData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-3 mt-6">
            {incidentData.slice(0, 4).map((entry, index) => (
              <div key={entry.name} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }}></div>
                  <span className="text-[11px] font-black text-slate-500 uppercase tracking-wider">{entry.name}</span>
                </div>
                <span className="font-black text-slate-800">{entry.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Alerts List */}
        <div className="bg-slate-900 rounded-[3rem] p-10 shadow-2xl text-white">
          <div className="flex justify-between items-center mb-8">
            <h3 className="font-black text-2xl tracking-tighter uppercase italic flex items-center gap-3"><Zap className="text-red-500" /> Flux d'Urgence</h3>
            <span className="px-4 py-2 bg-red-500/20 text-red-400 text-[10px] font-black rounded-xl uppercase tracking-widest">Temps Réel</span>
          </div>
          <div className="space-y-4 max-h-[400px] overflow-y-auto custom-scrollbar pr-4">
            {incidents.slice(0, 8).map(inc => (
              <div key={inc.id} className="p-6 bg-white/5 rounded-2xl border border-white/5 hover:bg-white/10 transition-all group">
                <div className="flex justify-between items-start mb-3">
                  <span className="text-sm font-black text-slate-200">{inc.siteName}</span>
                  <span className="text-[10px] text-slate-500 font-bold">{format(inc.timestamp, 'HH:mm')}</span>
                </div>
                <p className="text-xs text-slate-400 font-medium leading-relaxed mb-3 line-clamp-2">{inc.description}</p>
                <div className="flex items-center gap-2">
                  <span className="px-3 py-1 bg-red-600/20 text-red-400 text-[9px] font-black rounded-lg uppercase">{inc.type}</span>
                </div>
              </div>
            ))}
            {incidents.length === 0 && <div className="text-center py-10 opacity-30"><Shield size={48} className="mx-auto mb-4" /><p className="text-xs uppercase tracking-widest font-black">Aucun incident</p></div>}
          </div>
        </div>

        {/* Live Personnel */}
        <div className="bg-white rounded-[3rem] border border-slate-100 p-10 shadow-sm">
           <h3 className="font-black text-2xl tracking-tighter text-slate-800 uppercase mb-8">Personnel en Mission</h3>
           <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
             {agents.slice(0, 6).map(agent => (
               <div key={agent.id} className="p-5 bg-slate-50 rounded-2xl border border-slate-100 flex items-center gap-4">
                 <div className="w-12 h-12 bg-blue-600/10 rounded-xl flex items-center justify-center text-blue-600 font-black">{agent.firstName?.charAt(0)}{agent.lastName?.charAt(0)}</div>
                 <div className="flex-1">
                   <p className="text-xs font-black text-slate-800 uppercase tracking-tighter">{agent.firstName} {agent.lastName}</p>
                   <div className="flex items-center gap-2 mt-1">
                     <Battery size={14} className={agent.batteryLevel < 20 ? 'text-red-500' : 'text-emerald-500'} />
                     <span className="text-[10px] font-bold text-slate-500">{agent.batteryLevel || 0}%</span>
                   </div>
                 </div>
                 <div className={`w-2 h-2 rounded-full ${(agent.lastActive && (Date.now() - agent.lastActive < 300000)) ? 'bg-green-500 shadow-[0_0_10px_#22c55e]' : 'bg-slate-300'}`}></div>
               </div>
             ))}
           </div>
           <button onClick={() => onNavigate?.('agents')} className="w-full mt-8 py-4 bg-slate-50 text-slate-400 rounded-2xl font-black text-[10px] uppercase tracking-[4px] hover:bg-slate-100 transition-all">Voir tout l'effectif</button>
        </div>
      </div>
    </div>
  );
}

function SitesView({ sites, patrols, incidents, onPrint, onEdit, onDelete, onAdd }) {
  const [filter, setFilter] = useState('');
  const filtered = sites.filter(s => s.name.toLowerCase().includes(filter.toLowerCase()) || s.address.toLowerCase().includes(filter.toLowerCase()));

  return (
    <div className="space-y-12 animate-in fade-in duration-700 font-sans">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-8">
        <SectionHeader title="Parc Immobilier" subtitle={`${sites.length} sites sous haute surveillance`} icon={<Landmark size={32}/>} />
        <div className="flex items-center gap-4 w-full lg:w-auto">
          <div className="relative flex-1 lg:w-96"><Search className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-300" size={24} /><input value={filter} onChange={e => setFilter(e.target.value)} placeholder="Rechercher un site..." className="w-full pl-14 pr-6 py-5 bg-white border-2 border-slate-50 rounded-[1.75rem] outline-none font-bold text-slate-700 shadow-sm focus:border-blue-600 transition-all" /></div>
          <button onClick={onAdd} className="p-5 bg-blue-600 text-white rounded-[1.75rem] shadow-2xl shadow-blue-900/30 hover:bg-blue-700 transition-all active:scale-95"><Plus size={28}/></button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-10">
        {filtered.map(site => {
          const sitePatrols = patrols.filter(p => p.siteName === site.name);
          const siteIncidents = incidents.filter(i => i.siteName === site.name);
          const lastPatrol = sitePatrols[0];

          return (
            <div key={site.id} className="bg-white rounded-[3.5rem] border-2 border-slate-50 shadow-sm hover:shadow-2xl transition-all duration-500 overflow-hidden group flex flex-col md:flex-row">
              <div className="w-full md:w-72 bg-slate-50 p-8 flex flex-col justify-between relative overflow-hidden">
                <div className="relative z-10"><div className="p-4 bg-white rounded-2xl shadow-sm w-fit mb-6 text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-all duration-500"><Landmark size={28} /></div><h4 className="text-2xl font-black text-slate-800 tracking-tighter uppercase leading-none mb-3">{site.name}</h4><p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-relaxed line-clamp-3 mb-6">{site.address}</p></div>
                <div className="flex gap-2 relative z-10"><button onClick={() => onEdit(site)} className="p-3 bg-white text-slate-400 rounded-xl hover:text-blue-600 shadow-sm transition-all"><Edit size={18}/></button><button onClick={() => onDelete(site)} className="p-3 bg-white text-slate-400 rounded-xl hover:text-red-600 shadow-sm transition-all"><Trash2 size={18}/></button></div>
                <div className="absolute -right-4 -bottom-4 opacity-[0.03] text-slate-900 rotate-12 scale-[2] transition-all group-hover:rotate-0 group-hover:scale-[2.2]"><Landmark size={120}/></div>
              </div>
              <div className="flex-1 p-10 flex flex-col justify-between space-y-8">
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-1"><p className="text-[9px] font-black text-slate-300 uppercase tracking-widest">Points de Contrôle</p><div className="flex items-center gap-3"><ScanLine size={20} className="text-blue-500"/><span className="text-2xl font-black text-slate-800">{site.checkpoints?.length || 0}</span></div></div>
                  <div className="space-y-1"><p className="text-[9px] font-black text-slate-300 uppercase tracking-widest">Signalements</p><div className="flex items-center gap-3"><AlertTriangle size={20} className={siteIncidents.length > 0 ? 'text-red-500' : 'text-emerald-500'}/><span className="text-2xl font-black text-slate-800">{siteIncidents.length}</span></div></div>
                </div>
                <div className="bg-slate-50/50 rounded-3xl p-6 border border-slate-50 flex items-center justify-between">
                   <div className="flex items-center gap-4">
                     <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm"><Navigation size={20} className="text-slate-400" /></div>
                     <div><p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Dernière Mission</p><p className="text-xs font-black text-slate-700 uppercase">{lastPatrol ? format(lastPatrol.endTime, 'dd MMM • HH:mm') : 'Aucune donnée'}</p></div>
                   </div>
                   {lastPatrol && <div className={`px-4 py-2 rounded-xl text-[10px] font-black tracking-widest ${lastPatrol.pointsScanned === lastPatrol.pointsTotal ? 'bg-emerald-50 text-emerald-600' : 'bg-blue-50 text-blue-600'}`}>{((lastPatrol.pointsScanned/lastPatrol.pointsTotal)*100).toFixed(0)}%</div>}
                </div>
                <button onClick={() => onPrint(site)} className="w-full py-5 bg-slate-900 text-white rounded-[1.75rem] font-black text-[11px] uppercase tracking-[4px] shadow-xl hover:bg-blue-600 transition-all active:scale-95 flex items-center justify-center gap-3"><Printer size={20}/> Générer Planche QR</button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PatrolsArchivesView({ patrols, onViewDetails, uid, onPrintMonthly, onAction }) {
  const [filter, setFilter] = useState('');
  const filtered = patrols.filter(p => p.siteName.toLowerCase().includes(filter.toLowerCase()) || p.agentName.toLowerCase().includes(filter.toLowerCase()));
  const handleDelete = async (e, p) => { e.stopPropagation(); if(window.confirm(`Supprimer ?`)){ await deleteDoc(doc(db, 'users', uid, 'finished_patrols', p.id)); if(onAction) onAction('DELETE_PATROL', p.id); }};
  return (
    <div className="space-y-10 animate-in fade-in duration-700">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
        <SectionHeader title="Archives Missions" subtitle="Registre certifié des interventions" icon={<FileSearch size={32}/>} />
        <div className="flex items-center gap-4 bg-white p-3 rounded-[1.75rem] shadow-sm border border-slate-100">
          <button onClick={onPrintMonthly} className="bg-blue-600 text-white px-8 py-4 rounded-2xl font-black text-[10px] uppercase tracking-[3px] hover:bg-blue-700 flex items-center gap-3 shadow-xl"><FileText size={20}/> Bilan Mensuel</button>
          <div className="h-10 w-[2px] bg-slate-100"></div>
          <div className="relative w-72"><Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={20} /><input value={filter} onChange={e => setFilter(e.target.value)} placeholder="Rechercher..." className="w-full pl-12 pr-6 py-4 bg-slate-50 rounded-2xl text-sm outline-none font-bold text-slate-800" /></div>
        </div>
      </div>
      <div className="bg-white rounded-[3rem] border shadow-sm overflow-hidden"><table className="w-full text-left font-sans border-collapse">
        <thead className="bg-slate-50 text-[11px] font-black text-slate-400 uppercase tracking-[3px]"><tr><th className="px-10 py-8">Périmètre</th><th className="px-10 py-8">Responsable</th><th className="px-10 py-8 text-center">Score</th><th className="px-10 py-8">Horodatage</th><th className="px-10 py-8 text-right">Action</th></tr></thead>
        <tbody className="divide-y divide-slate-50">{filtered.map(p => (<tr key={p.id} className="hover:bg-blue-50/20 transition-all group cursor-pointer" onClick={() => onViewDetails(p)}>
              <td className="px-10 py-10 font-black text-slate-800 text-lg uppercase tracking-tighter leading-none">{p.siteName}</td>
              <td className="px-10 py-10 font-bold text-slate-500">{p.agentName}</td>
              <td className="px-10 py-10 text-center"><span className={`px-5 py-2.5 rounded-xl text-[10px] font-black tracking-[2px] ${p.pointsScanned === p.pointsTotal ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>{p.pointsScanned} / {p.pointsTotal} PTS</span></td>
              <td className="px-10 py-10 text-[11px] font-black text-slate-400 uppercase">{format(p.endTime, 'dd MMM yyyy HH:mm')}</td>
              <td className="px-10 py-10 text-right"><button onClick={(e) => handleDelete(e, p)} className="p-4 bg-red-50 text-red-500 rounded-2xl hover:bg-red-500 hover:text-white transition-all opacity-0 group-hover:opacity-100"><Trash2 size={20}/></button></td>
            </tr>))}</tbody></table></div>
    </div>
  );
}

function ManagementView({ title, items, col, icon, isUser, isCompany, uid, currentUser, companies, onAction, onOpenCompanyHub }) {
  const [showAdd, setShowAdd] = useState(false);
  const [formData, setFormData] = useState({});
  const isRoot = currentUser?.email === 'root@vigilus.app';

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const id = formData.id || Math.random().toString(36).substr(2, 9);
      const targetUid = (isUser && isRoot && formData.companyId) ? formData.companyId : uid;
      const dataToSave = { ...formData, id, companyId: targetUid };

      if (isUser && !formData.id && formData.role === 'USER') {
        const limit = companies.find(c => c.id === targetUid)?.userLimit || 50;
        if (items.filter(u => u.role === 'USER').length >= limit) { alert("Quota atteint."); return; }
      }

      if (isCompany) {
        dataToSave.userLimit = dataToSave.userLimit || 50;
        if (dataToSave.licenseType !== 'LIFE' && !dataToSave.expiryDate) { dataToSave.expiryDate = addYears(new Date(), dataToSave.licenseType === '1Y' ? 1 : 2).getTime(); }
      }

      await setDoc(doc(db, 'users', targetUid, col, id), dataToSave);
      if (isUser) {
        const virtualEmail = formData.username.includes('@') ? formData.username : `${formData.username}@vigilus.app`;
        await setDoc(doc(db, 'accounts', id), {
          companyId: targetUid,
          role: formData.role,
          email: virtualEmail
        });
      }

      if (onAction) onAction(formData.id ? 'UPDATE' : 'CREATE', `${title} : ${id}`);
      setShowAdd(false); setFormData({});
    } catch (err) { alert(err.message); }
  };

  return (
    <div className="space-y-12 font-sans animate-in fade-in duration-700">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <SectionHeader title={title} subtitle={`${items.length} unités actives`} icon={isUser ? <KeyRound size={32}/> : isCompany ? <Building2 size={32}/> : React.cloneElement(icon, {size: 32})} />
        <button onClick={() => { setFormData({}); setShowAdd(true); }} className="bg-blue-600 text-white px-10 py-5 rounded-[1.75rem] font-black flex items-center gap-4 shadow-2xl hover:bg-blue-700 transition-all hover:-translate-y-1 active:scale-95 uppercase tracking-[4px] text-[10px]"><Plus size={24} /> Créer {title.slice(0,-1)}</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
        {items.map(item => (
          <div key={item.id} onClick={() => isCompany && onOpenCompanyHub(item)} className={`bg-white p-10 rounded-[3rem] border border-slate-50 shadow-sm hover:shadow-2xl transition-all duration-500 group relative ${isCompany ? 'cursor-pointer hover:-translate-y-2' : ''}`}>
            <div className="flex justify-between items-start mb-10">
              <div className="p-5 bg-slate-50 rounded-2xl text-slate-400 group-hover:bg-blue-600 group-hover:text-white transition-all shadow-inner">{isUser ? <KeyRound size={32}/> : isCompany ? <Building2 size={32}/> : React.cloneElement(icon, {size: 32})}</div>
              <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-all translate-x-4 group-hover:translate-x-0">
                {isCompany && <div className="p-3 bg-blue-50 text-blue-600 rounded-xl font-black text-[10px] uppercase tracking-widest flex items-center gap-2 shadow-sm border border-blue-100"><SlidersHorizontal size={14}/> Gérer</div>}
                {!isCompany && <><button onClick={(e) => { e.stopPropagation(); setFormData(item); setShowAdd(true); }} className="p-3 bg-slate-50 text-slate-400 rounded-xl hover:bg-blue-50 hover:text-blue-600 transition-all border shadow-sm"><Edit size={18} /></button><button onClick={async (e) => { e.stopPropagation(); if(window.confirm("Supprimer ?")){ await deleteDoc(doc(db, 'users', uid, col, item.id)); if(onAction) onAction('DELETE', item.id); } }} className="p-3 bg-red-50 text-red-400 rounded-xl hover:bg-red-500 hover:text-white transition-all border shadow-sm"><Trash2 size={18} /></button></>}
              </div>
            </div>
            <h4 className="text-2xl font-black text-slate-800 uppercase tracking-tighter mb-2 leading-none">{isCompany ? item.name : `${item.firstName || item.username} ${item.lastName || ''}`}</h4>
            <p className="text-slate-400 text-xs font-black mb-10 uppercase tracking-[2px] leading-relaxed line-clamp-2">{isCompany ? (item.contactEmail || 'Pas d\'email') : `${item.function || item.role}`}</p>

            {col === 'agents' && (
              <div className="flex items-center gap-5 mb-10 bg-slate-50 p-5 rounded-3xl border border-slate-100 shadow-inner">
                <div className="flex items-center gap-2.5 font-black text-xs text-slate-700 uppercase"><Battery size={20} className={item.batteryLevel < 20 ? 'text-red-500' : 'text-emerald-500'} /> {item.batteryLevel || 0}%</div>
                <div className="flex items-center gap-2.5 ml-auto text-[10px] font-black text-slate-400 uppercase tracking-widest"><Clock size={16}/> {item.lastActive ? format(new Date(item.lastActive), 'HH:mm') : 'OFFLINE'}</div>
              </div>
            )}

            <div className="flex gap-3 items-center">
               {isUser && <span className="px-4 py-2 bg-blue-50 text-blue-600 rounded-xl text-[10px] font-black uppercase tracking-widest border border-blue-100">{item.role}</span>}
               {(isUser || isCompany) && <LicenseBadge expiryDate={item.expiryDate} type={item.licenseType} />}
               {isCompany && <span className="px-4 py-2 bg-slate-50 text-slate-400 rounded-xl text-[10px] font-black uppercase border border-slate-100">Quota: {item.userLimit}</span>}
            </div>
            {isCompany && <div className="absolute top-0 right-0 w-full h-full bg-blue-600/0 group-hover:bg-blue-600/5 transition-all rounded-[3rem]"></div>}
          </div>
        ))}
      </div>

      {showAdd && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xl z-[700] flex items-center justify-center p-4">
          <div className="bg-white rounded-[3.5rem] shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in duration-500">
            <div className="p-10 border-b border-slate-50 flex justify-between items-center bg-slate-50/50"><h3 className="text-xl font-black text-slate-800 uppercase tracking-tighter">{formData.id ? 'Mise à jour' : 'Nouvel Enregistrement'}</h3><button onClick={() => setShowAdd(false)} className="p-3 bg-white text-slate-400 rounded-full hover:text-red-500 transition-all shadow-xl"><X size={24}/></button></div>
            <form onSubmit={handleSave} className="p-12 space-y-6 max-h-[75vh] overflow-y-auto custom-scrollbar font-sans">
              {isCompany ? (
                <>
                  <InputField label="Raison Sociale" value={formData.name || ''} onChange={v => setFormData({...formData, name: v})} />
                  <InputField label="Email de Contact" value={formData.contactEmail || ''} onChange={v => setFormData({...formData, contactEmail: v})} />
                  <InputField label="Logo URL" value={formData.logoUrl || ''} onChange={v => setFormData({...formData, logoUrl: v})} />
                  <InputField label="Teinte Thème" value={formData.primaryColor || '#1e3a8a'} onChange={v => setFormData({...formData, primaryColor: v})} />
                  <InputField label="Quota Agents" value={formData.userLimit || 50} onChange={v => setFormData({...formData, userLimit: parseInt(v)})} />
                </>
              ) : isUser ? (
                <>
                  <div className="p-3 bg-blue-50 rounded-2xl border border-blue-100 text-xs font-black text-blue-900 uppercase">Accès Système</div>
                  <InputField label="Identifiant / Login" value={formData.username || ''} onChange={v => setFormData({...formData, username: v})} />
                  <InputField label="Mot de Passe" value={formData.password || ''} onChange={v => setFormData({...formData, password: v})} />

                  <div className="p-3 bg-slate-50 rounded-2xl border border-slate-100 text-xs font-black text-slate-800 uppercase mt-4">Identité de l'Agent</div>
                  <div className="grid grid-cols-2 gap-4">
                    <InputField label="Prénom" value={formData.firstName || ''} onChange={v => setFormData({...formData, firstName: v})} />
                    <InputField label="Nom de Famille" value={formData.lastName || ''} onChange={v => setFormData({...formData, lastName: v})} />
                  </div>
                  <InputField label="Fonction / Grade" value={formData.function || ''} onChange={v => setFormData({...formData, function: v})} />
                  <InputField label="Département" value={formData.department || ''} onChange={v => setFormData({...formData, department: v})} />
                  <InputField label="Téléphone" value={formData.phoneNumber || ''} onChange={v => setFormData({...formData, phoneNumber: v})} />

                  {isRoot && (
                    <select className="w-full p-5 bg-slate-50 rounded-2xl font-black outline-none border-2 border-transparent focus:border-blue-600 mb-4" value={formData.companyId || ''} onChange={e => setFormData({...formData, companyId: e.target.value})}>
                      <option value="">Sélectionner Compagnie...</option>
                      {companies.map(c => <option key={c.id} value={c.id}>{c.name.toUpperCase()}</option>)}
                    </select>
                  )}

                  <select className="w-full p-5 bg-slate-50 rounded-2xl font-black outline-none border-2 border-transparent focus:border-blue-600 transition-all" value={formData.role || 'USER'} onChange={e => setFormData({...formData, role: e.target.value})}>
                    <option value="USER">OPÉRATEUR (AGENT)</option>
                    <option value="SUPERVISOR">SUPERVISEUR</option>
                    <option value="COMPANY_ADMIN">ADMIN COMPAGNIE</option>
                    {isRoot && <option value="SUPER_ADMIN">SUPER ADMIN (MASTER)</option>}
                  </select>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-6">
                    <InputField label="Prénom" value={formData.firstName || ''} onChange={v => setFormData({...formData, firstName: v})} />
                    <InputField label="Nom de Famille" value={formData.lastName || ''} onChange={v => setFormData({...formData, lastName: v})} />
                  </div>
                  <InputField label="Fonction / Grade" value={formData.function || ''} onChange={v => setFormData({...formData, function: v})} />
                  <InputField label="Département" value={formData.department || ''} onChange={v => setFormData({...formData, department: v})} />
                  <InputField label="Téléphone" value={formData.phoneNumber || ''} onChange={v => setFormData({...formData, phoneNumber: v})} />
                </>
              )}
              <div className="flex gap-6 pt-12"><button type="button" onClick={() => setShowAdd(false)} className="flex-1 p-6 font-black text-slate-400 hover:bg-slate-50 transition-all uppercase text-xs tracking-[4px]">Annuler</button><button type="submit" className="flex-1 bg-blue-600 text-white p-6 rounded-2xl font-black shadow-2xl shadow-blue-900/30 hover:bg-blue-700 transition-all uppercase text-xs tracking-[4px]">Confirmer</button></div>
            </form></div></div>)}
    </div>
  );
}

function MapView({ incidents }) {
  const mapIncidents = incidents.filter(i => i.latitude && i.longitude);
  return (
    <div className="h-[calc(100vh-160px)] rounded-[4rem] overflow-hidden border-[12px] border-white shadow-2xl relative z-0 font-sans animate-in zoom-in duration-700 shadow-blue-900/10">
      <MapContainer center={mapIncidents[0] ? [mapIncidents[0].latitude, mapIncidents[0].longitude] : [48.85, 2.35]} zoom={13} style={{ height: '100%', width: '100%' }}>
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        {mapIncidents.map(inc => (
          <Marker key={inc.id} position={[inc.latitude, inc.longitude]}>
            <Popup>
              <div className="p-5 text-center font-sans">
                <div className="bg-red-600 text-white px-4 py-1.5 rounded-full text-[10px] font-black uppercase mb-3 inline-block tracking-widest shadow-lg shadow-red-900/20">{inc.type}</div>
                <p className="text-lg font-black text-slate-800 uppercase tracking-tighter">{inc.siteName}</p>
                <p className="text-[11px] font-bold text-slate-400 mt-3 leading-relaxed italic border-t pt-3 border-slate-50">{inc.description}</p>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
      {mapIncidents.length === 0 && <div className="absolute inset-0 bg-slate-900/10 flex items-center justify-center z-[1000] pointer-events-none backdrop-blur-[10px] transition-all"><div className="bg-white p-16 rounded-[4rem] shadow-[0_50px_100px_-20px_rgba(0,0,0,0.2)] font-black text-slate-400 uppercase tracking-[8px] border-8 border-white flex flex-col items-center gap-10 animate-pulse"><MapPinned size={80} className="text-slate-100" /> Aucun Signal Actif</div></div>}
    </div>
  );
}

function AuditLogsView({ logs }) {
  return (
    <div className="bg-white rounded-[3rem] border shadow-sm overflow-hidden animate-in fade-in slide-in-from-bottom-10 duration-700 font-sans">
      <div className="p-10 border-b border-slate-50 flex justify-between items-center bg-slate-50/30"><div><h3 className="text-2xl font-black text-slate-800 tracking-tight">Audit Trail</h3><p className="text-slate-400 font-bold text-[10px] uppercase tracking-[4px] mt-1 italic">Vigilus Ledger</p></div><div className="bg-white p-3 rounded-xl border shadow-sm text-blue-600"><Filter size={20}/></div></div>
      <div className="overflow-x-auto"><table className="w-full text-left font-sans border-collapse"><thead className="bg-slate-50/80 text-[10px] font-black text-slate-400 uppercase tracking-[2px]"><tr><th className="px-10 py-6">Responsable</th><th className="px-10 py-6">Événement</th><th className="px-10 py-6">Détails</th><th className="px-10 py-6 text-right">Horodatage</th></tr></thead><tbody className="divide-y divide-slate-50">{logs.map(log => (<tr key={log.id} className="hover:bg-slate-50/50 transition-colors"><td className="px-10 py-8"><div className="flex items-center gap-3"><div className="w-8 h-8 bg-slate-900 text-white rounded-full flex items-center justify-center text-[10px] font-black">{log.userName?.charAt(0).toUpperCase()}</div><span className="font-black text-slate-800">{log.userName}</span></div></td><td className="px-10 py-8 uppercase text-[10px] font-black"><span className={`px-4 py-2 rounded-xl border ${log.action?.includes('DELETE') ? 'bg-red-50 text-red-600 border-red-100' : log.action?.includes('CREATE') ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-blue-50 text-blue-600 border-blue-100'}`}>{log.action}</span></td><td className="px-10 py-8 text-sm text-slate-500 font-bold">{log.details}</td><td className="px-10 py-8 text-right font-black text-xs text-slate-400 uppercase">{log.timestamp ? format(new Date(log.timestamp), 'dd/MM/yy HH:mm') : '---'}</td></tr>))}</tbody></table></div>
    </div>
  );
}

function MaintenanceView({ onAction }) {
  const [minVersion, setMinVersion] = useState('');
  useEffect(() => { const f = async () => { const docSnap = await getDoc(doc(db, 'config', 'app_version')); if (docSnap.exists()) setMinVersion(docSnap.data().minimum_app_version || ''); }; f(); }, []);
  return (
    <div className="max-w-2xl mx-auto bg-white p-12 rounded-[4rem] border shadow-2xl font-sans text-center animate-in slide-in-from-bottom-12 duration-700 border-red-50">
      <div className="w-28 h-28 bg-red-50 text-red-600 rounded-[2.5rem] flex items-center justify-center mx-auto mb-10 shadow-inner border-4 border-white"><Shield size={56} /></div>
      <h3 className="text-4xl font-black text-slate-800 uppercase mb-4 tracking-tighter leading-none">Security Enforcement</h3>
      <p className="text-slate-400 font-bold uppercase text-[10px] tracking-[6px] mb-16">Version Controller</p>
      <div className="p-12 bg-slate-50 rounded-[3rem] border border-slate-100 mb-10 shadow-inner">
        <label className="text-[11px] font-black text-slate-500 uppercase mb-6 block tracking-[4px]">Build Code de Sécurité Minimal</label>
        <div className="flex gap-4"><input type="number" className="flex-1 p-6 bg-white rounded-[1.5rem] outline-none border-4 border-transparent focus:border-red-500 font-black text-4xl shadow-2xl text-center" value={minVersion} onChange={e => setMinVersion(e.target.value)} />
        <button onClick={async () => { await setDoc(doc(db, 'config', 'app_version'), { minimum_app_version: parseInt(minVersion), updatedAt: Date.now() }, { merge: true }); alert("Version système déployée !"); if(onAction) onAction('MAINTENANCE', `Version forcée: ${minVersion}`); }} className="bg-red-600 text-white px-10 py-6 rounded-[1.5rem] font-black uppercase text-xs tracking-widest shadow-2xl shadow-red-900/30 hover:bg-red-700 active:scale-95 transition-all">Déployer</button></div>
      </div>
      <p className="text-red-500 text-[10px] font-black uppercase tracking-[3px] italic animate-pulse px-16 leading-relaxed">Les agents ayant une version inférieure seront bloqués instantanément.</p>
    </div>
  );
}

function MonthlyReportView({ patrols, incidents, company, onClear }) {
  const [month, setMonth] = useState(format(new Date(), 'yyyy-MM'));
  const list = patrols.filter(p => format(new Date(p.endTime), 'yyyy-MM') === month);
  const totalScanned = list.reduce((s,p)=>s+p.pointsScanned,0);
  const totalPoints = list.reduce((s,p)=>s+p.pointsTotal,0);
  const score = totalPoints > 0 ? ((totalScanned/totalPoints)*100).toFixed(1) : 0;
  return (
    <div className="fixed inset-0 bg-white z-[800] overflow-y-auto p-12 font-sans animate-in slide-in-from-bottom duration-700">
      <div className="max-w-5xl mx-auto"><div className="flex justify-between items-center mb-12 print:hidden border-b pb-8"><div className="flex items-center gap-6"><h3 className="text-3xl font-black uppercase tracking-tighter leading-none">Bilan Mensuel<br/><span className="text-blue-600 text-sm tracking-widest">Générateur Automatique</span></h3><input type="month" value={month} onChange={e => setMonth(e.target.value)} className="p-4 border-4 border-slate-50 rounded-[1.5rem] font-black text-lg shadow-xl outline-none focus:border-blue-600 transition-all bg-white" /></div><div className="flex gap-5"><button onClick={onClear} className="px-10 py-5 rounded-2xl font-black bg-slate-100 uppercase text-xs tracking-widest">Abandonner</button><button onClick={() => window.print()} className="px-10 py-5 bg-blue-600 text-white rounded-2xl shadow-2xl shadow-blue-900/30 font-black flex items-center gap-4 uppercase text-xs tracking-[4px] hover:bg-blue-700"><Printer size={24}/> Exporter PDF</button></div></div>
        <div className="print:block p-20 border-[24px] border-slate-900 rounded-[5rem] bg-white shadow-[0_100px_100px_-50px_rgba(0,0,0,0.2)] relative overflow-hidden"><div className="absolute top-0 right-0 w-[40rem] h-[40rem] bg-slate-50 rounded-full translate-x-1/2 -translate-y-1/2 -z-10"></div>
          <div className="flex justify-between items-start mb-32 relative z-10"><div>{company?.logoUrl ? <img src={company.logoUrl} className="h-28 w-auto mb-12" alt="Logo" /> : <div className="bg-slate-900 text-white px-10 py-4 rounded-full text-[20px] font-black uppercase mb-10 inline-block tracking-[12px]">VIGILUS PRO</div>}<h1 className="text-8xl font-black text-slate-900 uppercase tracking-tighter leading-[0.75] mb-8">RAPPORT<br/>MENSUEL<br/>CERTIFIÉ</h1><div className="flex items-center gap-4 bg-blue-600 text-white px-6 py-3 rounded-2xl w-fit shadow-2xl shadow-blue-900/30"><p className="text-2xl font-black uppercase tracking-[8px]">{format(new Date(month), 'MMMM yyyy', { locale: fr })}</p></div></div><div className="text-right text-[12px] font-black text-slate-400 uppercase tracking-[6px]"><p>Certificat d'Audit #V-{format(new Date(), 'yyyyMMdd')}</p><p className="text-slate-900 text-4xl font-black mt-6 tracking-tighter leading-none">VIGILUS PRO<br/>SYSTEM</p></div></div>
          <div className="grid grid-cols-3 gap-12 mb-32 relative z-10"><div className="bg-slate-50 p-16 rounded-[3.5rem] text-center shadow-inner border-2 border-white"><p className="text-[12px] font-black text-slate-400 uppercase mb-6 tracking-[6px]">Missions Terrain</p><p className="text-8xl font-black text-slate-900 tracking-tighter leading-none">{list.length}</p></div><div className="bg-slate-50 p-16 rounded-[3.5rem] text-center shadow-inner border-2 border-white"><p className="text-[12px] font-black text-slate-400 uppercase mb-6 tracking-[6px]">Alertes Levées</p><p className="text-8xl font-black text-red-600 tracking-tighter leading-none">{list.reduce((s,p)=>s+(p.incidents?.length||0),0)}</p></div><div className="bg-slate-50 p-16 rounded-[3.5rem] text-center shadow-inner border-2 border-white"><p className="text-[12px] font-black text-slate-400 uppercase mb-6 tracking-[6px]">Indice de Vigilance</p><p className="text-8xl font-black text-blue-600 tracking-tighter leading-none">{score}%</p></div></div>
          <div className="space-y-10 relative z-10"><h4 className="text-4xl font-black text-slate-900 uppercase border-b-[12px] border-slate-900 pb-6 mb-16 tracking-tighter">REGISTRE DÉTAILLÉ DES INTERVENTIONS</h4><table className="w-full text-left font-sans mb-32 border-collapse"><thead><tr className="text-[12px] font-black text-slate-400 uppercase border-b-4 border-slate-100"><th className="py-10">Périmètre Supervisé</th><th className="py-10">Personnel de Sécurité</th><th className="py-10 text-center">Précision</th><th className="py-10 text-right">Date & Horodatage</th></tr></thead><tbody className="divide-y-4 divide-slate-50">{list.map(p => (<tr key={p.id}><td className="py-10 font-black uppercase text-lg tracking-tighter leading-none">{p.siteName}</td><td className="py-10 text-lg font-bold text-slate-500">{p.agentName}</td><td className="py-10 text-center font-black text-blue-600 text-2xl tracking-tighter">{p.pointsScanned}/{p.pointsTotal}</td><td className="py-10 text-right text-xs font-black text-slate-400 uppercase tracking-[2px]">{format(new Date(p.endTime), 'dd/MM/yy HH:mm')}</td></tr>))}</tbody></table></div>
          <div className="mt-40 pt-20 border-t-8 border-slate-50 flex justify-between uppercase text-[14px] font-black text-slate-300 relative z-10 px-10">\r\n            <div className="text-center w-96"><p className="mb-24 tracking-[8px]">Sceau de la Compagnie</p><div className="h-56 border-4 border-dashed border-slate-100 rounded-[3rem] flex items-center justify-center text-[12px] italic uppercase tracking-[5px]">Cachet Authentique</div></div>\r\n            <div className="text-center w-96"><p className="mb-24 tracking-[8px]">Visa de Direction</p><div className="h-56 border-4 border-dashed border-slate-100 rounded-[3rem] flex items-center justify-center text-[12px] italic uppercase tracking-[5px]">Signature Autorisée</div></div>\r\n          </div><p className="text-center text-[10px] font-black text-slate-200 uppercase tracking-[15px] mt-32 leading-loose">VIGILUS PRO • SECURE AUDIT LEDGER • 2026<br/>HIGH-END SECURITY CLOUD SOLUTIONS</p></div></div></div>
  );
}

function PrintView({ site, onClear }) {
  return (
    <div className="fixed inset-0 bg-white z-[900] overflow-y-auto p-12 print:p-0 font-sans animate-in slide-in-from-bottom duration-500">
      <div className="max-w-4xl mx-auto"><div className="flex justify-between items-center mb-12 print:hidden border-b pb-8"><div><h3 className="text-4xl font-black text-slate-800 uppercase tracking-tighter">Identification QR : {site.name}</h3><p className="text-slate-400 font-bold text-sm uppercase mt-2 tracking-[4px]">Planche d'Impression Haute Résolution</p></div><div className="flex gap-4"><button onClick={onClear} className="px-8 py-4 rounded-2xl font-black bg-slate-100 uppercase text-xs tracking-widest">Annuler</button><button onClick={() => window.print()} className="px-8 py-4 bg-blue-600 text-white rounded-2xl shadow-2xl font-black flex items-center gap-3 uppercase text-xs tracking-widest hover:bg-blue-700 transition-all"><Printer size={20}/> Imprimer</button></div></div>
        <div className="grid grid-cols-2 gap-16 print:grid-cols-2">{site.checkpoints?.map((cp) => (<div key={cp.id} className="border-[10px] border-slate-900 rounded-[4rem] p-16 flex flex-col items-center justify-center text-center bg-white break-inside-avoid relative shadow-2xl"><div className="mb-12 text-center relative w-full"><div className="bg-slate-900 text-white px-10 py-3 rounded-full text-[16px] font-black uppercase mb-6 inline-block tracking-[10px]">VIGILUS PRO</div><div className="h-[2px] bg-slate-900 absolute top-[50%] left-0 w-full -z-10 opacity-10"></div><p className="text-[12px] font-black text-slate-400 uppercase tracking-widest mt-4">Système de Sécurité Certifié</p></div><div className="p-8 bg-white border-4 border-slate-50 rounded-[3.5rem] shadow-[inset_0_0_50px_rgba(0,0,0,0.05)] mb-10"><QRCodeCanvas value={cp.qrCode} size={280} level="H" includeMargin={false} /></div><h4 className="text-5xl font-black text-slate-900 mt-6 uppercase tracking-tighter">{cp.name}</h4><p className="text-xl font-bold text-slate-500 mt-4 uppercase tracking-[4px] border-t-2 pt-4 border-slate-50">{site.name}</p></div>))}</div></div>
    </div>
  );
}

function PatrolDetailView({ patrol, onClose }) {
  return (
    <div className="fixed inset-0 z-[600] flex justify-end font-sans">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md" onClick={onClose}></div>
      <div className="w-full max-w-3xl bg-white h-full shadow-2xl relative z-10 flex flex-col animate-in slide-in-from-right duration-500 rounded-l-[4rem]">
        <header className="p-12 border-b border-slate-50 bg-slate-50/30 flex justify-between items-center"><div><span className="text-[10px] font-black text-blue-600 bg-blue-50 px-4 py-2 rounded-full uppercase tracking-[4px] mb-4 inline-block shadow-sm">Rapport de Mission Certifié</span><h3 className="text-4xl font-black text-slate-800 tracking-tighter uppercase">{patrol.siteName}</h3></div><button onClick={onClose} className="p-4 bg-white text-slate-400 rounded-full hover:text-red-500 transition-all shadow-xl"><X size={32}/></button></header>
        <div className="flex-1 overflow-y-auto p-12 space-y-12 custom-scrollbar">
          <div className="grid grid-cols-2 gap-6"><DetailCard icon={<Users size={20}/>} label="Intervenant" value={patrol.agentName} /><DetailCard icon={<Clock size={20}/>} label="Fin de Mission" value={format(patrol.endTime, 'HH:mm:ss')} /><div className="bg-blue-600 p-8 rounded-[2.5rem] shadow-2xl shadow-blue-900/30 text-white flex flex-col items-center justify-center text-center"><p className="text-[10px] font-black uppercase tracking-[3px] opacity-60 mb-2">Indice de Précision</p><p className="text-4xl font-black tracking-tighter">{patrol.pointsScanned} / {patrol.pointsTotal}</p></div><DetailCard icon={<Calendar size={20}/>} label="Date Archive" value={format(patrol.endTime, 'dd MMMM yyyy', { locale: fr })} /></div>
          <div className="space-y-6 pt-10"><h4 className="text-sm font-black text-slate-800 uppercase tracking-[4px] border-b-2 border-slate-100 pb-4 flex items-center gap-3"><AlertTriangle size={20} className="text-red-600" /> Observations du Terrain ({patrol.incidents?.length || 0})</h4><div className="space-y-6">{patrol.incidents?.map((inc, i) => (<div key={i} className="p-8 bg-slate-50 rounded-[2.5rem] border-2 border-white shadow-xl"><div className="flex justify-between mb-6"><span className="px-5 py-2 bg-white border-2 border-red-100 text-[10px] font-black rounded-xl text-red-600 uppercase tracking-widest shadow-sm">{inc.type}</span><span className="text-[11px] font-bold text-slate-400">{format(inc.timestamp, 'HH:mm')}</span></div><p className="text-slate-800 font-bold text-lg leading-relaxed tracking-tight">{inc.description}</p>{inc.photoPath && <div className="mt-8 p-3 bg-blue-900 text-white rounded-2xl text-[10px] font-black uppercase text-center tracking-[5px] shadow-2xl flex items-center justify-center gap-4 hover:bg-blue-800 transition-all cursor-pointer"><FileText size={18}/> Voir Preuve Visuelle</div>}</div>))}</div></div>
        </div>
        <footer className="p-10 border-t flex gap-6 bg-slate-50/50"><button className="flex-1 bg-white border-4 border-slate-100 p-6 rounded-[2rem] font-black text-slate-700 flex items-center justify-center gap-4 hover:bg-slate-100 transition-all uppercase text-xs tracking-widest shadow-lg shadow-black/5"><Download size={24}/> EXCEL</button><button className="flex-1 bg-blue-600 text-white p-6 rounded-[2rem] font-black flex items-center justify-center gap-4 hover:bg-blue-700 transition-all uppercase text-xs tracking-widest shadow-2xl shadow-blue-900/30"><Printer size={24}/> RAPPORT PDF</button></footer>
      </div></div>
  );
}

function CompanyHubModal({ company, uid, onClose, onAction, companies }) {
  const [activeHubTab, setActiveHubTab] = useState('menu');
  const [formData, setFormData] = useState(company);
  const [copied, setCopied] = useState(false);

  const currentUser = auth.currentUser;
  const isRoot = currentUser?.email === 'root@vigilus.app';

  // Data states for the specific company
  const [patrols, setPatrols] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [agents, setAgents] = useState([]);
  const [sites, setSites] = useState([]);
  const [appUsers, setAppUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [printingSite, setPrintingSite] = useState(null);
  const [selectedPatrol, setSelectedPatrol] = useState(null);
  const [showAddSite, setShowAddSite] = useState(false);
  const [editingSite, setEditingSite] = useState(null);

  useEffect(() => {
    if (!company.id) return;
    const cid = company.id;
    const q1 = onSnapshot(query(collection(db, 'users', cid, 'finished_patrols'), orderBy('endTime', 'desc')), s => setPatrols(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    const q2 = onSnapshot(query(collection(db, 'users', cid, 'standalone_incidents'), orderBy('timestamp', 'desc')), s => setIncidents(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    const q3 = onSnapshot(collection(db, 'users', cid, 'agents'), s => setAgents(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    const q4 = onSnapshot(collection(db, 'users', cid, 'sites'), s => setSites(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    const q5 = onSnapshot(collection(db, 'users', cid, 'users'), s => setAppUsers(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    const q6 = onSnapshot(query(collection(db, 'users', cid, 'audit_logs'), orderBy('timestamp', 'desc')), s => setAuditLogs(s.docs.map(d => ({ id: d.id, ...d.data() }))));

    return () => { q1(); q2(); q3(); q4(); q5(); q6(); };
  }, [company.id]);

  const save = async () => {
    await setDoc(doc(db, 'users', uid, 'companies', company.id), formData, { merge: true });
    alert("Configurations sauvegardées.");
  };

  const deleteComp = async () => {
    if(window.confirm("Supprimer définitivement cette entreprise et TOUTES ses données ?")) {
       await deleteDoc(doc(db, 'users', uid, 'companies', company.id));
       onClose();
    }
  };

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard Live', icon: <LayoutDashboard size={40}/>, color: 'blue', desc: 'Statistiques et performance' },
    { id: 'map', label: 'Cartographie GPS', icon: <MapPinned size={40}/>, color: 'indigo', desc: 'Suivi géographique temps réel' },
    { id: 'sites', label: 'Parc Immobilier', icon: <Landmark size={40}/>, color: 'emerald', desc: 'Gestion des sites et points QR' },
    { id: 'agents', label: 'Ressources Humaines', icon: <ShieldCheck size={40}/>, color: 'cyan', desc: 'Gestion du personnel terrain' },
    { id: 'users', label: 'Accès & Terminaux', icon: <KeyRound size={40}/>, color: 'violet', desc: 'Comptes et permissions' },
    { id: 'patrols', label: 'Historique Missions', icon: <FileSearch size={40}/>, color: 'amber', desc: 'Archives des rapports certifiés' },
    { id: 'audit', label: 'Audit Trail', icon: <History size={40}/>, color: 'slate', desc: 'Journal de sécurité complet' },
    { id: 'profile', label: 'Configuration', icon: <Settings size={40}/>, color: 'rose', desc: 'Identité, Licences et Sécurité' },
  ];

  return (
    <div className="fixed inset-0 bg-slate-900/95 backdrop-blur-3xl z-[1000] flex items-center justify-center p-0 animate-in fade-in duration-300">
      <div className="bg-[#f8fafc] w-full h-full flex flex-col overflow-hidden">

        {/* HEADER */}
        <header className="h-24 bg-white border-b px-12 flex justify-between items-center shrink-0 shadow-sm z-20">
           <div className="flex items-center gap-6">
             <button onClick={activeHubTab === 'menu' ? onClose : () => setActiveHubTab('menu')} className="p-3 hover:bg-slate-100 rounded-2xl transition-all text-slate-400 hover:text-slate-900">
               {activeHubTab === 'menu' ? <X size={28}/> : <ChevronRight size={28} className="rotate-180"/>}
             </button>
             <div className="h-10 w-[1px] bg-slate-200"></div>
             <div className="flex items-center gap-4">
               {formData.logoUrl ? <img src={formData.logoUrl} className="h-10 w-auto" alt="Logo" /> : <div className="p-2 bg-blue-600 rounded-xl text-white"><Shield size={20}/></div>}
               <div>
                 <h3 className="text-xl font-black text-slate-900 uppercase tracking-tighter leading-none">{company.name}</h3>
                 <p className="text-[9px] font-black text-slate-400 uppercase tracking-[3px] mt-1">Management Console</p>
               </div>
             </div>
           </div>

           {activeHubTab !== 'menu' && (
             <nav className="hidden xl:flex items-center gap-2 bg-slate-100 p-1.5 rounded-2xl">
                {menuItems.map(item => (
                  <button
                    key={item.id}
                    onClick={() => setActiveHubTab(item.id)}
                    className={`px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${activeHubTab === item.id ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                  >
                    {item.label}
                  </button>
                ))}
             </nav>
           )}

           <div className="flex items-center gap-4">
             <button onClick={onClose} className="bg-red-50 text-red-500 px-6 py-3 rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-red-500 hover:text-white transition-all shadow-sm">Fermer Session</button>
           </div>
        </header>

        <div className="flex-1 overflow-y-auto custom-scrollbar">
          {activeHubTab === 'menu' ? (
            <div className="max-w-7xl mx-auto p-12 py-20">
               <div className="mb-16 text-center">
                 <h2 className="text-5xl font-black text-slate-900 tracking-tighter uppercase mb-4">Que voulez-vous gérer ?</h2>
                 <p className="text-slate-400 font-bold uppercase text-xs tracking-[6px]">Sélectionnez une rubrique opérationnelle</p>
               </div>

               <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                 {menuItems.map(item => (
                   <button
                     key={item.id}
                     onClick={() => setActiveHubTab(item.id)}
                     className="group bg-white p-10 rounded-[3.5rem] border border-slate-100 shadow-sm hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 text-left relative overflow-hidden"
                   >
                     <div className={`w-20 h-20 rounded-[2rem] bg-${item.color}-600/10 flex items-center justify-center text-${item.color}-600 mb-8 group-hover:scale-110 transition-transform duration-500`}>
                       {item.icon}
                     </div>
                     <h4 className="text-xl font-black text-slate-800 uppercase tracking-tighter mb-2">{item.label}</h4>
                     <p className="text-xs text-slate-400 font-medium leading-relaxed uppercase tracking-wider">{item.desc}</p>
                     <div className={`absolute -right-4 -bottom-4 opacity-[0.03] text-slate-900 rotate-12 scale-[2.5] group-hover:rotate-0 transition-all duration-1000`}>
                       {item.icon}
                     </div>
                   </button>
                 ))}
               </div>

               <div className="mt-20 pt-20 border-t flex justify-between items-center">
                  <div className="flex items-center gap-8">
                    <div className="text-center">
                      <p className="text-3xl font-black text-slate-800">{agents.length}</p>
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Agents</p>
                    </div>
                    <div className="h-8 w-[1px] bg-slate-200"></div>
                    <div className="text-center">
                      <p className="text-3xl font-black text-slate-800">{sites.length}</p>
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Sites</p>
                    </div>
                    <div className="h-8 w-[1px] bg-slate-200"></div>
                    <div className="text-center">
                      <p className="text-3xl font-black text-slate-800">{patrols.length}</p>
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Missions</p>
                    </div>
                  </div>
                  <button onClick={deleteComp} className="flex items-center gap-3 text-red-400 hover:text-red-600 font-black text-[10px] uppercase tracking-widest transition-colors"><Trash2 size={18}/> Supprimer l'entreprise</button>
               </div>
            </div>
          ) : (
            <div className="p-12 animate-in fade-in slide-in-from-bottom-4 duration-500 max-w-[1600px] mx-auto">
              {activeHubTab === 'dashboard' && <DashboardView patrols={patrols} agents={agents} sites={sites} incidents={incidents} onNavigate={setActiveHubTab} />}
              {activeHubTab === 'map' && <MapView incidents={incidents} />}
              {activeHubTab === 'sites' && <SitesView sites={sites} patrols={patrols} incidents={incidents} onPrint={setPrintingSite} onEdit={(s) => { setEditingSite(s); setShowAddSite(true); }} onDelete={async (s) => { if(window.confirm("Supprimer ce site ?")) await deleteDoc(doc(db, 'users', company.id, 'sites', s.id)); }} onAdd={() => { setEditingSite(null); setShowAddSite(true); }} />}
              {activeHubTab === 'agents' && <ManagementView title="Personnel" items={agents} col="agents" icon={<ShieldCheck/>} uid={company.id} currentUser={currentUser} companies={[]} onAction={onAction} />}
            {activeHubTab === 'users' && <ManagementView title="Utilisateurs" items={appUsers} col="users" icon={<KeyRound/>} isUser={true} uid={company.id} currentUser={currentUser} companies={companies} onAction={onAction} />}
              {activeHubTab === 'patrols' && <PatrolsArchivesView patrols={patrols} onViewDetails={setSelectedPatrol} uid={company.id} onPrintMonthly={() => {}} onAction={onAction} />}
              {activeHubTab === 'audit' && <AuditLogsView logs={auditLogs} />}

              {activeHubTab === 'profile' && (
                <div className="space-y-12">
                  <SectionHeader title="Paramètres Entreprise" subtitle="Identité et quotas de licence" icon={<Settings size={32}/>} />
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                    <Card title="Identité Visuelle" icon={<Palette className="text-blue-600" size={24} />}>
                      <div className="space-y-6">
                        <InputField label="Raison Sociale" value={formData.name} onChange={v => setFormData({...formData, name: v})} />
                        <InputField label="Email de Contact" value={formData.contactEmail} onChange={v => setFormData({...formData, contactEmail: v})} />
                        <InputField label="Lien Logo (HTTPS)" value={formData.logoUrl} onChange={v => setFormData({...formData, logoUrl: v})} />
                        <InputField label="Code Hex Couleur" value={formData.primaryColor} onChange={v => setFormData({...formData, primaryColor: v})} />
                        <button onClick={save} className="w-full bg-blue-600 text-white p-6 rounded-2xl font-black uppercase text-xs tracking-widest shadow-xl">Sauvegarder l'identité</button>
                      </div>
                    </Card>

                    <Card title="Gestion des Droits" icon={<Award className="text-amber-500" size={24} />}>
                      <div className="space-y-8">
                        <div className="space-y-3"><label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Quota d'Agents</label><input type="number" className="w-full p-6 bg-slate-50 rounded-[1.75rem] font-black text-4xl outline-none focus:border-blue-600 border-2 border-transparent transition-all" value={formData.userLimit} onChange={e => setFormData({...formData, userLimit: parseInt(e.target.value)})} /></div>
                        <div className="space-y-3"><label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Modèle de Licence</label><select className="w-full p-6 bg-slate-50 rounded-[1.75rem] font-black text-xl outline-none focus:border-blue-600 border-2 border-transparent transition-all" value={formData.licenseType} onChange={e => setFormData({...formData, licenseType: e.target.value})}><option value="1Y">BUSINESS (1 AN)</option><option value="2Y">ENTERPRISE (2 ANS)</option><option value="LIFE">INFINITY (ILLIMITÉ)</option></select></div>
                        <div className="p-8 bg-slate-900 rounded-3xl text-white relative overflow-hidden">
                           <div className="relative z-10">
                             <p className="text-[10px] font-black text-blue-400 uppercase tracking-widest mb-3">Clef d'Activation</p>
                             <code className="text-2xl font-black tracking-widest block mb-4">{formData.licenseKey || 'NON GÉNÉRÉE'}</code>
                             <button onClick={() => {
                                const key = `VPRO-${formData.name.substring(0,3).toUpperCase()}-${formData.userLimit}-${Math.random().toString(36).substring(2,10).toUpperCase()}`;
                                setFormData({...formData, licenseKey: key});
                              }} className="bg-white/10 hover:bg-white/20 text-white px-6 py-2 rounded-xl text-[10px] font-black uppercase transition-all">Régénérer</button>
                           </div>
                        </div>
                        <button onClick={save} className="w-full bg-slate-900 text-white p-6 rounded-2xl font-black uppercase text-xs tracking-widest shadow-xl">Appliquer les quotas</button>
                      </div>
                    </Card>
                  </div>

                  <div className="bg-red-50 p-10 rounded-[3rem] border-2 border-red-100 flex items-center gap-10">
                    <div className="p-6 bg-red-100 text-red-600 rounded-[2rem] shadow-inner"><Lock size={40}/></div>
                    <div><h5 className="text-xl font-black text-red-800 uppercase tracking-tighter mb-2">Blocage Manuel d'Urgence</h5><p className="text-sm font-bold text-red-600/60 uppercase leading-relaxed">Suspendre instantanément tous les accès mobiles et web de cette compagnie.</p></div>
                    <button className="bg-red-600 text-white px-10 py-5 rounded-2xl font-black uppercase text-xs tracking-widest shadow-xl ml-auto">VERROUILLER</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {printingSite && <PrintView site={printingSite} onClear={() => setPrintingSite(null)} />}
        {selectedPatrol && <PatrolDetailView patrol={selectedPatrol} onClose={() => setSelectedPatrol(null)} />}
        {showAddSite && <AddSiteModal site={editingSite} uid={company.id} onClose={() => setShowAddSite(false)} />}
      </div>
    </div>
  );
}

function HubNavItem({ active, icon, label, onClick }) {
  return (
    <button onClick={onClick} className={`flex items-center gap-4 w-full p-5 rounded-3xl transition-all font-black text-xs uppercase tracking-widest ${active ? 'bg-white text-blue-600 shadow-xl shadow-blue-900/5 translate-x-2 border border-blue-50' : 'text-slate-400 hover:bg-white/50 hover:text-slate-600'}`}>
      {React.cloneElement(icon, { size: 20 })} <span>{label}</span>
    </button>
  );
}

function AddSiteModal({ site, uid, onClose }) {
  const [formData, setFormData] = useState(site ? { isChronological: true, ...site } : { name: '', address: '', isChronological: true });
  const [autoPoints, setAutoPoints] = useState(0);

  const save = async (e) => {
    e.preventDefault();
    const id = formData.id || Math.random().toString(36).substr(2, 9);
    const data = { isChronological: true, ...formData, id };
    if (!data.checkpoints) data.checkpoints = [];

    if (autoPoints > 0) {
       const startNum = data.checkpoints.length + 1;
       for (let i = 0; i < autoPoints; i++) {
         const currentNum = startNum + i;
         const numStr = currentNum.toString().padStart(2, '0');
         data.checkpoints.push({
           id: Math.random().toString(36).substr(2, 9),
           name: `Point ${numStr}`,
           qrCode: `QR_${id.toUpperCase()}_${numStr}`
         });
       }
    }
    await setDoc(doc(db, 'users', uid, 'sites', id), data);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xl z-[700] flex items-center justify-center p-4">
      <div className="bg-white rounded-[3.5rem] shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in duration-500">
        <div className="p-10 border-b border-slate-50 flex justify-between items-center bg-slate-50/50"><h3 className="text-xl font-black text-slate-800 uppercase tracking-tighter">{site ? 'Modifier Site' : 'Nouveau Site'}</h3><button onClick={onClose} className="p-3 bg-white text-slate-400 rounded-full hover:text-red-500 transition-all shadow-xl"><X size={24}/></button></div>
        <form onSubmit={save} className="p-12 space-y-6">
          <InputField label="Libellé du Site" value={formData.name || ''} onChange={v => setFormData({...formData, name: v})} />
          <InputField label="Adresse Postale" value={formData.address || ''} onChange={v => setFormData({...formData, address: v})} />

          <div className="p-6 bg-slate-50 rounded-3xl border border-slate-100 flex items-center justify-between">
            <div>
              <label className="text-xs font-black text-slate-800 uppercase block">Ordre Chronologique Strict</label>
              <span className="text-[10px] text-slate-400 font-bold block">{formData.isChronological !== false ? "Séquence imposée (1 ➔ 2 ➔ 3...)" : "Ordre libre (Scans libres)"}</span>
            </div>
            <input
              type="checkbox"
              className="w-6 h-6 rounded-lg text-blue-600 focus:ring-blue-500"
              checked={formData.isChronological !== false}
              onChange={e => setFormData({...formData, isChronological: e.target.checked})}
            />
          </div>

          <div className="p-8 bg-blue-50 rounded-3xl border-2 border-blue-100 mt-6 shadow-inner text-center">
            <label className="text-[11px] font-black text-blue-800 uppercase block mb-4 tracking-widest">
              {formData.id ? "Ajouter des Points QR" : "Générateur Automatique de Points"}
            </label>
            <input
              type="number"
              placeholder="0"
              className="w-24 p-5 bg-white rounded-2xl outline-none font-black text-3xl shadow-2xl text-center border-4 border-blue-200 focus:border-blue-600"
              value={autoPoints || ''}
              onChange={e => setAutoPoints(parseInt(e.target.value) || 0)}
            />
            {formData.id && <p className="text-[9px] text-blue-400 font-bold mt-4 uppercase italic">Actuellement: {formData.checkpoints?.length || 0} points</p>}
          </div>

          <div className="flex gap-6 pt-12"><button type="button" onClick={onClose} className="flex-1 p-6 font-black text-slate-400 hover:bg-slate-50 transition-all uppercase text-xs tracking-[4px]">Annuler</button><button type="submit" className="flex-1 bg-blue-600 text-white p-6 rounded-2xl font-black shadow-2xl shadow-blue-900/30 hover:bg-blue-700 transition-all uppercase text-xs tracking-[4px]">Confirmer</button></div>
        </form>
      </div>
    </div>
  );
}

// --- SETTINGS VIEW ---

function SettingsView({ uid, currentUser, appUsers }) {
  const [newPass, setNewPass] = useState('');
  const [confirmPass, setConfirmPass] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetConfirm, setResetConfirm] = useState(false);
  const agentsCount = appUsers.filter(u => u.role === 'USER').length;

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    if (newPass !== confirmPass) {
      alert("Les mots de passe ne correspondent pas.");
      return;
    }
    if (newPass.length < 6) {
      alert("Le mot de passe doit contenir au moins 6 caractères.");
      return;
    }

    setLoading(true);
    try {
      await updatePassword(currentUser, newPass);

      // Mise à jour de la collection Firestore users pour cohérence
      if (uid) {
        await setDoc(doc(db, 'users', uid, 'users', currentUser.uid), {
          password: newPass
        }, { merge: true });
      }

      alert("Mot de passe mis à jour avec succès !");
      setNewPass('');
      setConfirmPass('');
    } catch (error) {
      console.error(error);
      if (error.code === 'auth/requires-recent-login') {
        alert("Cette opération est sensible et nécessite une connexion récente. Veuillez vous déconnecter et vous reconnecter avant de réessayer.");
      } else {
        alert("Erreur lors de la mise à jour : " + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-12 font-sans animate-in fade-in duration-700">
      <SectionHeader title="Paramétrages" subtitle="Sécurité et préférences du compte" icon={<Settings size={32}/>} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <Card title="Sécurité du Compte" icon={<Lock className="text-blue-600" size={24} />}>
          <form onSubmit={handleUpdatePassword} className="space-y-6">
            <InputField
              label="Nouveau Mot de Passe"
              type="password"
              value={newPass}
              onChange={setNewPass}
              placeholder="••••••••"
            />
            <InputField
              label="Confirmer le Mot de Passe"
              type="password"
              value={confirmPass}
              onChange={setConfirmPass}
              placeholder="••••••••"
            />
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-slate-900 text-white p-6 rounded-[2rem] font-black uppercase text-xs tracking-[4px] shadow-2xl flex items-center justify-center gap-4 hover:bg-black transition-all disabled:opacity-50"
            >
              {loading ? <Loader2 className="animate-spin" size={20} /> : "Mettre à jour le mot de passe"}
            </button>
          </form>
        </Card>

        <Card title="Informations de Session" icon={<Activity className="text-emerald-600" size={24} />}>
          <div className="space-y-6">
            <DetailCard icon={<Mail size={18}/>} label="Email Actuel" value={currentUser?.email} />
            <DetailCard icon={<KeyRound size={18}/>} label="ID Utilisateur" value={currentUser?.uid} />
            <div className="p-6 bg-blue-50 rounded-3xl border border-blue-100 italic text-[10px] font-black uppercase text-blue-600 tracking-widest leading-relaxed">
              VIGILUS PRO CLOUD v3.1 - Console de Supervision Opérationnelle
            </div>
          </div>
        </Card>
      </div>

      <div className="bg-red-50 p-12 rounded-[3rem] border-4 border-red-100 flex flex-col lg:flex-row items-center gap-10 shadow-lg group">
         <div className="flex-1 text-center lg:text-left"><h3 className="text-2xl font-black text-red-800 uppercase mb-3 tracking-tighter leading-none">Périmètre Critique</h3><p className="text-red-600/70 text-sm font-black uppercase tracking-widest leading-relaxed">Réinitialisation intégrale de l'environnement. Cette action est irréversible.</p></div>
         <button onClick={() => setResetConfirm(true)} className="w-full lg:w-auto bg-red-600 text-white px-12 py-6 rounded-[2rem] font-black text-[11px] uppercase tracking-[5px] shadow-2xl hover:bg-red-700 transition-all">RESET INSTANCE</button>
      </div>
      {resetConfirm && <div className="fixed inset-0 bg-red-900/80 backdrop-blur-2xl z-[900] flex items-center justify-center p-8"><div className="bg-white rounded-[4rem] p-16 max-w-xl w-full shadow-2xl text-center border-[12px] border-red-600 animate-in zoom-in duration-300">\r\n         <div className="w-32 h-32 bg-red-100 text-red-600 rounded-[2.5rem] flex items-center justify-center mx-auto mb-10 shadow-inner rotate-12 group-hover:rotate-0 transition-all"><AlertCircle size={72} /></div>\r\n         <h4 className="text-4xl font-black text-slate-800 mb-6 tracking-tighter uppercase leading-none">ACTION LÉGALE REQUISE</h4>\r\n         <p className="text-slate-500 mb-12 font-bold text-lg leading-relaxed px-10 uppercase tracking-tighter">Vous êtes sur le point de détruire l'intégralité des données. Aucune récupération possible.</p>\r\n         <div className="flex gap-6"><button onClick={() => setResetConfirm(false)} className="flex-1 p-6 font-black bg-slate-100 text-slate-400 rounded-3xl uppercase text-xs tracking-[4px]">Abandonner</button>\r\n            <button onClick={async () => { alert("Support technique requis."); setResetConfirm(false); }} className="flex-1 p-6 bg-red-600 text-white font-black rounded-3xl shadow-2xl uppercase text-xs tracking-[4px]">Exécuter</button>\r\n         </div></div></div>}
    </div>
  );
}

function Card({ title, icon, children }) {
  return (
    <div className="bg-white p-10 rounded-[3rem] border border-slate-50 shadow-sm relative overflow-hidden">
      <div className="flex items-center gap-4 mb-10">
        <div className="p-4 bg-slate-50 rounded-2xl">{icon}</div>
        <h3 className="text-xl font-black text-slate-800 uppercase tracking-tighter">{title}</h3>
      </div>
      {children}
    </div>
  );
}

// --- LICENSE FACTORY ---

function computeOwnerHash(ownerName) {
  const clean = (ownerName || "").trim().toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (!clean) return "VIGI";
  let h1 = 0, h2 = 0;
  for (let i = 0; i < clean.length; i++) {
    const code = clean.charCodeAt(i);
    h1 = (h1 * 31 + code * (i + 1)) % 1296;
    h2 = (h2 * 17 + code * (i + 3)) % 1296;
  }
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  const c1 = chars[Math.floor(h1 / 36) % 36];
  const c2 = chars[h1 % 36];
  const c3 = chars[Math.floor(h2 / 36) % 36];
  const c4 = chars[h2 % 36];
  return `${c1}${c2}${c3}${c4}`;
}

function EditLicenseModal({ license, onClose }) {
  const [formData, setFormData] = useState({
    owner: license.owner || '',
    licenseType: license.licenseType || '1Y',
    status: license.status || 'UNUSED'
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const cleanKey = license.cleanKey || license.key.replace(/-/g, "").toUpperCase();
      const updatedData = {
        owner: formData.owner.trim(),
        licenseType: formData.licenseType,
        status: formData.status,
        updatedAt: Date.now()
      };

      await setDoc(doc(db, 'generated_licenses', cleanKey), updatedData, { merge: true });
      await setDoc(doc(db, 'activated_licenses', cleanKey), updatedData, { merge: true });

      alert("Licence mise à jour avec succès !");
      onClose();
    } catch (err) {
      console.error(err);
      alert("Erreur lors de la mise à jour : " + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xl z-[800] flex items-center justify-center p-4 animate-in fade-in duration-300">
      <div className="bg-white rounded-[3.5rem] shadow-2xl w-full max-w-md overflow-hidden border border-white/20">
        <div className="p-8 border-b border-slate-100 flex justify-between items-center bg-slate-50">
          <h3 className="text-lg font-black text-slate-800 uppercase tracking-tighter">Éditer la Licence</h3>
          <button onClick={onClose} className="p-3 bg-white text-slate-400 rounded-full hover:text-red-500 transition-all shadow-md"><X size={20}/></button>
        </div>
        <form onSubmit={handleSave} className="p-8 space-y-6">
          <div className="p-4 bg-slate-100 rounded-2xl font-mono text-center font-black text-blue-600 text-lg">
            {license.key}
          </div>

          <InputField label="Détenteur Légal / Client" value={formData.owner} onChange={v => setFormData({...formData, owner: v})} required />

          <div>
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">Durée de Validité</label>
            <input
              type="text"
              value={formData.licenseType}
              onChange={e => setFormData({...formData, licenseType: e.target.value})}
              className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 outline-none focus:border-blue-600"
              placeholder="Ex: 1Y, 2Y, Y03, M06, LIFE"
            />
          </div>

          <div>
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">Statut de la Licence</label>
            <select
              value={formData.status}
              onChange={e => setFormData({...formData, status: e.target.value})}
              className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 outline-none focus:border-blue-600"
            >
              <option value="UNUSED">Disponible (DISPONIBLE)</option>
              <option value="USED">Activée / Utilisée (ACTIVÉE)</option>
              <option value="REVOKED">Révoquée / Bloquée (RÉVOQUÉE)</option>
            </select>
          </div>

          <div className="flex gap-4 pt-4">
            <button type="button" onClick={onClose} className="flex-1 p-5 font-black text-slate-400 hover:bg-slate-50 transition-all uppercase text-xs tracking-widest rounded-2xl">Annuler</button>
            <button type="submit" disabled={saving} className="flex-1 bg-blue-600 text-white p-5 rounded-2xl font-black uppercase text-xs tracking-widest shadow-xl hover:bg-blue-700 transition-all disabled:opacity-50">
              {saving ? <Loader2 className="animate-spin mx-auto" size={20}/> : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function KeyGeneratorView() {
  const [generatedKeys, setGeneratedKeys] = useState([]);
  const [ownerName, setOwnerName] = useState('');
  const [type, setType] = useState('1Y');
  const [userLimit, setUserLimit] = useState(100);
  const [isCustomDuration, setIsCustomDuration] = useState(false);
  const [customNum, setCustomNum] = useState("1");
  const [customUnit, setCustomUnit] = useState("Y");
  const [packCount, setPackCount] = useState(1);
  const [copied, setCopied] = useState(false);
  const [licensesList, setLicensesList] = useState([]);
  const [editingLicense, setEditingLicense] = useState(null);
  const [selectedKeys, setSelectedKeys] = useState([]);

  const effectiveTypeCode = isCustomDuration
    ? `${customUnit}${String(Math.min(Math.max(parseInt(customNum) || 1, 1), 99)).padStart(2, '0')}`
    : type;

  useEffect(() => {
    const q = query(collection(db, 'generated_licenses'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, s => {
      setLicensesList(s.docs.map(d => ({ id: d.id, ...d.data() })));
    }, err => console.error(err));
    return () => unsub();
  }, []);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedKeys(licensesList.map(l => l.id));
    } else {
      setSelectedKeys([]);
    }
  };

  const handleToggleSelect = (id) => {
    if (selectedKeys.includes(id)) {
      setSelectedKeys(selectedKeys.filter(k => k !== id));
    } else {
      setSelectedKeys([...selectedKeys, id]);
    }
  };

  const handlePrintSelectedPdf = () => {
    const selectedLics = licensesList.filter(l => selectedKeys.includes(l.id));
    if (selectedLics.length === 0) return;

    const printWindow = window.open('', '_blank');
    const dateStr = new Date().toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Certificat de Licences - Sélection (${selectedLics.length})</title>
        <style>
          body { font-family: 'Segoe UI', sans-serif; padding: 40px; color: #0d1b2a; background: #fff; }
          .header { border-bottom: 4px solid #d4af37; padding-bottom: 20px; margin-bottom: 30px; display: flex; justify-content: space-between; align-items: center; }
          .logo { font-size: 26px; font-weight: 900; color: #0d1b2a; }
          .logo span { color: #2563eb; }
          .title { font-size: 20px; font-weight: 900; text-transform: uppercase; margin-bottom: 20px; }
          .meta { background: #f8fafc; padding: 20px; border-radius: 12px; border-left: 6px solid #d4af37; margin-bottom: 30px; }
          .meta p { margin: 6px 0; font-size: 13px; font-weight: 700; }
          .keys-table { width: 100%; border-collapse: collapse; margin-bottom: 40px; }
          .keys-table th { background: #0d1b2a; color: #fff; text-align: left; padding: 12px; font-size: 11px; text-transform: uppercase; }
          .keys-table td { padding: 12px; border-bottom: 1px solid #e2e8f0; font-family: monospace; font-size: 15px; font-weight: 800; }
          .footer { border-top: 1px solid #cbd5e1; pt: 20px; font-size: 10px; color: #64748b; text-align: center; margin-top: 40px; }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo">VIGILUS <span>PRO</span></div>
          <div style="font-size: 11px; font-weight: 800; color: #64748b;">SECURITY SYSTEMS</div>
        </div>
        <div class="title">CERTIFICAT D'ACTIVATION SÉLECTIONNÉ</div>
        <div class="meta">
          <p>NOMBRE DE LICENCES SÉLECTIONNÉES : <span style="color: #2563eb;">${selectedLics.length}</span></p>
          <p>DATE D'ÉMISSION : ${dateStr}</p>
        </div>
        <table class="keys-table">
          <thead>
            <tr>
              <th>#</th>
              <th>CLIENT DÉTENTEUR</th>
              <th>CLÉ DE LICENCE À USAGE UNIQUE</th>
              <th>DURÉE</th>
              <th>STATUT</th>
            </tr>
          </thead>
          <tbody>
            ${selectedLics.map((l, i) => `
              <tr>
                <td>0${i + 1}</td>
                <td style="font-family: sans-serif;">${l.owner || '-'}</td>
                <td>${l.key}</td>
                <td style="font-family: sans-serif;">${l.licenseType}</td>
                <td style="color: ${l.status === 'USED' ? '#dc2626' : '#16a34a'}; font-family: sans-serif;">${l.status === 'USED' ? 'ACTIVÉE' : 'DISPONIBLE'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        <div class="footer">
          <p>Document officiel généré par Vigilus Security Systems.</p>
          <p>© ${new Date().getFullYear()} Vigilus Security Systems - Tous droits réservés.</p>
        </div>
        <script>window.onload = () => { window.print(); };</script>
      </body>
      </html>
    `;
    printWindow.document.write(html);
    printWindow.document.close();
  };

  const handleExportSelectedCsv = () => {
    const selectedLics = licensesList.filter(l => selectedKeys.includes(l.id));
    if (selectedLics.length === 0) return;

    let csv = "CLIENT;CLE_LICENCE;DUREE;STATUT\n";
    selectedLics.forEach(l => {
      csv += `"${l.owner || 'Client'}";"${l.key}";"${l.licenseType}";"${l.status}"\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Licences_Vigilus_Selection_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDeleteSelected = async () => {
    if (!window.confirm(`Voulez-vous vraiment supprimer les ${selectedKeys.length} licences sélectionnées ?`)) return;
    const selectedLics = licensesList.filter(l => selectedKeys.includes(l.id));
    for (const lic of selectedLics) {
      const cleanKey = lic.cleanKey || lic.key.replace(/-/g, "").toUpperCase();
      try {
        await deleteDoc(doc(db, 'generated_licenses', cleanKey));
        await deleteDoc(doc(db, 'activated_licenses', cleanKey));
      } catch (e) {}
    }
    setSelectedKeys([]);
    alert("Licences sélectionnées supprimées !");
  };

  const handleDeleteLicense = async (lic) => {
    if (!window.confirm(`Voulez-vous vraiment supprimer la licence ${lic.key} (${lic.owner}) ?`)) return;
    try {
      const cleanKey = lic.cleanKey || lic.key.replace(/-/g, "").toUpperCase();
      await deleteDoc(doc(db, 'generated_licenses', cleanKey));
      await deleteDoc(doc(db, 'activated_licenses', cleanKey));
      alert("Licence supprimée de la base.");
    } catch (e) {
      console.error(e);
      alert("Erreur lors de la suppression : " + e.message);
    }
  };

  useEffect(() => {
    const q = query(collection(db, 'generated_licenses'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, s => {
      setLicensesList(s.docs.map(d => ({ id: d.id, ...d.data() })));
    }, err => console.error(err));
    return () => unsub();
  }, []);

  const generateSingleKey = (typeCode, owner) => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    const ownerHash = computeOwnerHash(owner);

    let dur = 'Y01';
    const upperCode = (typeCode || '').toUpperCase().trim();
    if (upperCode === 'LIFE' || upperCode === 'L00') {
      dur = 'L00';
    } else if (upperCode.startsWith('Y') || upperCode.startsWith('M')) {
      const u = upperCode.substring(0, 1);
      const n = Math.min(Math.max(parseInt(upperCode.substring(1)) || 1, 1), 99);
      dur = `${u}${String(n).padStart(2, '0')}`;
    } else if (upperCode.endsWith('Y')) {
      const n = Math.min(Math.max(parseInt(upperCode.replace('Y','')) || 1, 1), 99);
      dur = `Y${String(n).padStart(2, '0')}`;
    } else if (upperCode.endsWith('M')) {
      const n = Math.min(Math.max(parseInt(upperCode.replace('M','')) || 1, 1), 99);
      dur = `M${String(n).padStart(2, '0')}`;
    }

    let attempts = 0;
    while (attempts < 5000) {
      let randomPart = '';
      for (let i = 0; i < 4; i++) {
        randomPart += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      const cleanKey = `VIG${dur}${ownerHash}${randomPart}`;
      let sum = 0;
      for (let i = 0; i < cleanKey.length; i++) {
        sum += cleanKey.charCodeAt(i);
      }

      if (sum % 5 === 0) {
        return `VIG-${dur}-${ownerHash}-${randomPart}`;
      }
      attempts++;
    }
    return null;
  };

  const generatePack = async () => {
    if (!ownerName.trim()) {
      alert("Le nom du Client / Entreprise est OBLIGATOIRE.");
      return;
    }
    const count = parseInt(packCount) || 1;
    const keys = [];
    const packId = `PACK_${Date.now()}`;

    for (let i = 0; i < count; i++) {
      const key = generateSingleKey(effectiveTypeCode, ownerName);
      if (key) {
        keys.push(key);
        const cleanKey = key.replace(/-/g, "").toUpperCase();
        try {
          await setDoc(doc(db, 'generated_licenses', cleanKey), {
            key,
            cleanKey,
            owner: ownerName.trim(),
            licenseType: effectiveTypeCode,
            userLimit: parseInt(userLimit) || 100,
            packId,
            status: 'UNUSED',
            createdAt: Date.now()
          }, { merge: true });
        } catch (e) {
          console.error("Firestore License Registration Error:", e);
        }
      }
    }
    setGeneratedKeys(keys);
  };

  return (
    <div className="max-w-3xl mx-auto space-y-10 animate-in fade-in duration-700">
      <SectionHeader title="Générateur de Packs & Licences" subtitle="Abonnements Liés au Nom du Client" icon={<KeyRound size={32}/>} />

      <div className="bg-white p-12 rounded-[4rem] border shadow-2xl text-center">
        <div className="w-24 h-24 bg-blue-50 text-blue-600 rounded-[2.5rem] flex items-center justify-center mx-auto mb-8 shadow-inner">
          <Award size={48} />
        </div>

        <div className="mb-8 text-left">
          <InputField
            label="Nom du Client / Entreprise (MANDATOIRE)"
            value={ownerName}
            onChange={v => { setOwnerName(v); setGeneratedKeys([]); }}
            placeholder="Ex: SECURITAS SARL"
            required
          />
        </div>

        <div className="mb-8 text-left">
          <div className="flex justify-between items-center mb-3">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Durée de Validité au Choix</label>
            <button
              onClick={() => { setIsCustomDuration(!isCustomDuration); setGeneratedKeys([]); }}
              className="text-[10px] font-black text-blue-600 hover:underline uppercase tracking-wider"
            >
              {isCustomDuration ? "Préréglages" : "Saisie Sur Mesure"}
            </button>
          </div>

          {!isCustomDuration ? (
            <div className="flex flex-wrap gap-3">
              {[
                { id: '1Y', label: '1 AN' },
                { id: '2Y', label: '2 ANS' },
                { id: '3Y', label: '3 ANS' },
                { id: '5Y', label: '5 ANS' },
                { id: '10Y', label: '10 ANS' },
                { id: '6M', label: '6 MOIS' },
                { id: 'LIFE', label: 'À VIE (LIFE)' }
              ].map(t => (
                <button
                  key={t.id}
                  onClick={() => { setType(t.id); setGeneratedKeys([]); }}
                  className={`px-5 py-3 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all border-2 ${type === t.id ? 'bg-blue-600 text-white border-blue-600 shadow-lg' : 'bg-slate-50 text-slate-500 border-transparent hover:bg-slate-100'}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          ) : (
            <div className="flex items-center gap-4 bg-slate-50 p-4 rounded-3xl border border-slate-100">
              <input
                type="number"
                min="1"
                max="99"
                value={customNum}
                onChange={e => { setCustomNum(e.target.value); setGeneratedKeys([]); }}
                className="w-24 p-3 bg-white font-black text-xl text-center rounded-2xl border-2 border-slate-200 outline-none focus:border-blue-600"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => { setCustomUnit('Y'); setGeneratedKeys([]); }}
                  className={`px-5 py-3 rounded-2xl font-black text-[10px] uppercase tracking-widest border-2 ${customUnit === 'Y' ? 'bg-blue-600 text-white border-blue-600 shadow-md' : 'bg-white text-slate-500 border-slate-200'}`}
                >
                  Année(s)
                </button>
                <button
                  type="button"
                  onClick={() => { setCustomUnit('M'); setGeneratedKeys([]); }}
                  className={`px-5 py-3 rounded-2xl font-black text-[10px] uppercase tracking-widest border-2 ${customUnit === 'M' ? 'bg-blue-600 text-white border-blue-600 shadow-md' : 'bg-white text-slate-500 border-slate-200'}`}
                >
                  Mois
                </button>
              </div>
              <span className="text-xs font-black text-blue-600 uppercase ml-auto">Code: {effectiveTypeCode}</span>
            </div>
          )}
        </div>

        <div className="mb-8 text-left">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2 block mb-3">Quota d'Utilisateurs Interconnectés par Licence</label>
          <div className="flex flex-wrap gap-3">
            {[50, 100, 200, 500, 1000].map(q => (
              <button
                key={q}
                type="button"
                onClick={() => setCustomNum ? setUserLimit(q) : null}
                className={`px-5 py-3 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all border-2 ${userLimit === q ? 'bg-blue-600 text-white border-blue-600 shadow-lg' : 'bg-slate-50 text-slate-500 border-transparent hover:bg-slate-100'}`}
              >
                {q} Utilisateurs
              </button>
            ))}
          </div>
        </div>

        <div className="mb-10 text-left">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2 block mb-3">Taille du Pack (Nombre de licences)</label>
          <div className="flex flex-wrap gap-3">
            {[1, 5, 10, 20, 50].map(c => (
              <button
                key={c}
                onClick={() => { setPackCount(c); setGeneratedKeys([]); }}
                className={`px-6 py-3 rounded-2xl font-black text-[11px] uppercase tracking-widest transition-all border-2 ${packCount === c ? 'bg-slate-900 text-white border-slate-900 shadow-lg' : 'bg-slate-50 text-slate-500 border-transparent hover:bg-slate-100'}`}
              >
                {c === 1 ? '1 Clé Unique' : `Pack de ${c}`}
              </button>
            ))}
          </div>
        </div>

        {generatedKeys.length > 0 && (
          <div className="p-10 bg-slate-900 rounded-[3rem] border-8 border-slate-800 mb-10 text-left relative overflow-hidden group">
            <div className="flex justify-between items-center mb-6">
              <div>
                <p className="text-[10px] font-black text-blue-400 uppercase tracking-widest">Détenteur Légal: {ownerName}</p>
                <p className="text-xs font-black text-white uppercase">{generatedKeys.length === 1 ? 'Clé Unique' : `Pack de ${generatedKeys.length} Licences`} ({effectiveTypeCode})</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    const text = `CLIENT: ${ownerName}\nDURÉE: ${effectiveTypeCode}\n\nLICENCES:\n` + generatedKeys.join("\n");
                    navigator.clipboard.writeText(text);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  }}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-3 rounded-2xl text-[10px] font-black uppercase transition-all shadow-lg"
                >
                  {copied ? <CheckCircle2 size={16} /> : <Copy size={16} />}
                  {copied ? 'Copie Réussie' : 'Copier'}
                </button>
                <button
                  onClick={() => {
                    const printWindow = window.open('', '_blank');
                    const dateStr = new Date().toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
                    const html = `
                      <!DOCTYPE html>
                      <html>
                      <head>
                        <title>Certificat de Licences - ${ownerName}</title>
                        <style>
                          body { font-family: 'Segoe UI', sans-serif; padding: 40px; color: #0d1b2a; background: #fff; }
                          .header { border-bottom: 4px solid #d4af37; padding-bottom: 20px; margin-bottom: 30px; display: flex; justify-content: space-between; align-items: center; }
                          .logo { font-size: 26px; font-weight: 900; color: #0d1b2a; }
                          .logo span { color: #2563eb; }
                          .title { font-size: 20px; font-weight: 900; text-transform: uppercase; margin-bottom: 20px; }
                          .meta { background: #f8fafc; padding: 20px; border-radius: 12px; border-left: 6px solid #d4af37; margin-bottom: 30px; }
                          .meta p { margin: 6px 0; font-size: 13px; font-weight: 700; }
                          .keys-table { width: 100%; border-collapse: collapse; margin-bottom: 40px; }
                          .keys-table th { background: #0d1b2a; color: #fff; text-align: left; padding: 12px; font-size: 11px; text-transform: uppercase; }
                          .keys-table td { padding: 12px; border-bottom: 1px solid #e2e8f0; font-family: monospace; font-size: 15px; font-weight: 800; }
                          .footer { border-top: 1px solid #cbd5e1; pt: 20px; font-size: 10px; color: #64748b; text-align: center; margin-top: 40px; }
                        </style>
                      </head>
                      <body>
                        <div class="header">
                          <div class="logo">VIGILUS <span>PRO</span></div>
                          <div style="font-size: 11px; font-weight: 800; color: #64748b;">SECURITY SYSTEMS</div>
                        </div>
                        <div class="title">CERTIFICAT D'ACTIVATION OFFICIEL</div>
                        <div class="meta">
                          <p>DÉTENTEUR LÉGAL : <span style="color: #2563eb;">${ownerName.toUpperCase()}</span></p>
                          <p>VALIDITÉ : ${effectiveTypeCode}</p>
                          <p>DATE D'ÉMISSION : ${dateStr}</p>
                        </div>
                        <table class="keys-table">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>CLÉ DE LICENCE À USAGE UNIQUE</th>
                              <th>STATUT INITIAL</th>
                            </tr>
                          </thead>
                          <tbody>
                            ${generatedKeys.map((k, i) => `
                              <tr>
                                <td>0${i + 1}</td>
                                <td>${k}</td>
                                <td style="color: #16a34a;">DISPONIBLE (1 USAGE)</td>
                              </tr>
                            `).join('')}
                          </tbody>
                        </table>
                        <div class="footer">
                          <p>Chaque clé est à usage unique et liée au nom du client ${ownerName}.</p>
                          <p>© ${new Date().getFullYear()} Vigilus Security Systems - Tous droits réservés.</p>
                        </div>
                        <script>window.onload = () => { window.print(); };</script>
                      </body>
                      </html>
                    `;
                    printWindow.document.write(html);
                    printWindow.document.close();
                  }}
                  className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-5 py-3 rounded-2xl text-[10px] font-black uppercase transition-all shadow-lg"
                >
                  <Printer size={16} />
                  <span>Télécharger PDF</span>
                </button>
              </div>
            </div>

            <div className="max-h-60 overflow-y-auto space-y-2 font-mono text-lg font-black text-white bg-black/40 p-6 rounded-2xl border border-white/10 tracking-widest">
              {generatedKeys.map((k, idx) => (
                <div key={idx} className="flex justify-between items-center py-1 border-b border-white/5 last:border-none">
                  <span>{k}</span>
                  <span className="text-[10px] font-sans font-black text-blue-400">#0{idx + 1}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <button
          onClick={generatePack}
          disabled={!ownerName.trim()}
          className="w-full bg-blue-600 text-white p-7 rounded-[2rem] font-black uppercase text-xs tracking-[5px] shadow-2xl shadow-blue-900/30 hover:bg-blue-700 active:scale-95 transition-all flex items-center justify-center gap-4 disabled:opacity-40"
        >
          <Zap size={20} /> GÉNÉRER {packCount > 1 ? `PACK DE ${packCount} LICENCES` : 'LA LICENCE'} ({effectiveTypeCode})
        </button>

        <div className="mt-12 pt-10 border-t border-slate-50 flex flex-col items-center gap-2">
           <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Empreinte Déterministe d'Identité VIG-V3</p>
           <p className="text-[8px] text-slate-300 italic uppercase">L'activation sur l'APK vérifie le nom du détenteur et la clé.</p>
        </div>
      </div>

      {licensesList.length > 0 && (
        <div className="bg-white p-10 rounded-[3.5rem] border shadow-xl space-y-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <h3 className="text-xl font-black text-slate-800 uppercase tracking-tighter">
              Suivi des Packs et Licences ({licensesList.length})
            </h3>
            {selectedKeys.length > 0 && (
              <div className="flex flex-wrap items-center gap-3 bg-blue-50 p-3 px-6 rounded-2xl border border-blue-200 animate-in fade-in duration-300">
                <span className="text-xs font-black text-blue-900 uppercase">{selectedKeys.length} sélectionnée(s)</span>
                <button
                  onClick={handlePrintSelectedPdf}
                  className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-xl text-[10px] font-black uppercase transition-all shadow-md"
                >
                  <Printer size={14} /> PDF Sélection
                </button>
                <button
                  onClick={handleExportSelectedCsv}
                  className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-xl text-[10px] font-black uppercase transition-all shadow-md"
                >
                  <Download size={14} /> Excel/CSV
                </button>
                <button
                  onClick={handleDeleteSelected}
                  className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-xl text-[10px] font-black uppercase transition-all shadow-md"
                >
                  <Trash2 size={14} /> Supprimer
                </button>
              </div>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs font-sans">
              <thead>
                <tr className="border-b border-slate-100 text-[10px] font-black uppercase text-slate-400 tracking-wider">
                  <th className="p-3 w-10">
                    <input
                      type="checkbox"
                      className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 cursor-pointer"
                      checked={selectedKeys.length > 0 && selectedKeys.length === licensesList.length}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th className="p-3">Client Détenteur</th>
                  <th className="p-3">Clé de Licence</th>
                  <th className="p-3">Durée</th>
                  <th className="p-3">Statut Usage</th>
                  <th className="p-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {licensesList.map(lic => {
                  const isSelected = selectedKeys.includes(lic.id);
                  return (
                    <tr key={lic.id} className={`border-b border-slate-50 hover:bg-slate-50/50 ${isSelected ? 'bg-blue-50/40' : ''}`}>
                      <td className="p-3">
                        <input
                          type="checkbox"
                          className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 cursor-pointer"
                          checked={isSelected}
                          onChange={() => handleToggleSelect(lic.id)}
                        />
                      </td>
                      <td className="p-3 font-bold text-slate-800">{lic.owner || 'Client Standalone'}</td>
                      <td className="p-3 font-mono font-bold text-blue-600">{lic.key}</td>
                      <td className="p-3 font-bold text-slate-500">{lic.licenseType}</td>
                      <td className="p-3 font-black">
                        {lic.status === 'USED' ? (
                          <span className="bg-red-100 text-red-600 px-3 py-1 rounded-full text-[9px] uppercase tracking-widest">Activée / Utilisée</span>
                        ) : lic.status === 'REVOKED' ? (
                          <span className="bg-slate-200 text-slate-700 px-3 py-1 rounded-full text-[9px] uppercase tracking-widest">Révoquée</span>
                        ) : (
                          <span className="bg-emerald-100 text-emerald-600 px-3 py-1 rounded-full text-[9px] uppercase tracking-widest">Disponible (1 usage)</span>
                        )}
                      </td>
                      <td className="p-3 text-right">
                        <div className="flex justify-end gap-2">
                          <button onClick={() => setEditingLicense(lic)} className="p-2 bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-100 transition-all" title="Modifier">
                            <Edit size={16} />
                          </button>
                          <button onClick={() => handleDeleteLicense(lic)} className="p-2 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition-all" title="Supprimer">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editingLicense && <EditLicenseModal license={editingLicense} onClose={() => setEditingLicense(null)} />}
    </div>
  );
}

function LicenseFactory({ company, onClose }) {
  const [params, setParams] = useState({ agents: 50, duration: 12, features: 'FULL' });
  const [generated, setGenerated] = useState(null);
  const [copied, setCopied] = useState(false);

  const generate = () => {
    const key = `VPRO-${company.name.substring(0,3).toUpperCase()}-${params.agents}-${Math.random().toString(36).substring(2,10).toUpperCase()}`;
    setGenerated(key);
  };

  const saveToCompany = async () => {
    const expiry = addMonths(new Date(), params.duration).getTime();
    await setDoc(doc(db, 'users', auth.currentUser.uid, 'companies', company.id), {
      userLimit: parseInt(params.agents),
      licenseType: params.duration >= 24 ? 'ENTERPRISE' : 'BUSINESS',
      expiryDate: expiry,
      licenseKey: generated
    }, { merge: true });
    alert("Licence appliquée au client !");
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-2xl z-[1000] flex items-center justify-center p-6 animate-in fade-in duration-300">
      <div className="bg-white rounded-[4rem] shadow-2xl w-full max-w-2xl overflow-hidden border border-white/20">
        <div className="p-12 bg-slate-900 text-white relative">
           <div className="relative z-10">
             <h3 className="text-3xl font-black uppercase tracking-tighter mb-2">Générateur de Licences</h3>
             <p className="text-blue-500 font-black uppercase text-[10px] tracking-[4px]">Vigilus PRO Factory</p>
           </div>
           <Award className="absolute right-10 top-10 text-white/5" size={120} />
        </div>
        <div className="p-12 space-y-8 font-sans">
          <div className="grid grid-cols-2 gap-8">
            <div className="space-y-4">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Quota d'Agents (Plage)</label>
              <input type="number" className="w-full p-5 bg-slate-50 rounded-2xl font-black text-2xl outline-none focus:border-blue-600 border-2 border-transparent transition-all" value={params.agents} onChange={e => setParams({...params, agents: e.target.value})} />
            </div>
            <div className="space-y-4">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Validité (Mois)</label>
              <select className="w-full p-5 bg-slate-50 rounded-2xl font-black text-xl outline-none focus:border-blue-600 border-2 border-transparent transition-all" value={params.duration} onChange={e => setParams({...params, duration: parseInt(e.target.value)})}>
                <option value="1">1 MOIS (TEST)</option><option value="6">6 MOIS</option><option value="12">12 MOIS (1 AN)</option><option value="24">24 MOIS (2 ANS)</option><option value="120">120 MOIS (10 ANS)</option>
              </select>
            </div>
          </div>
          {!generated ? (
            <button onClick={generate} className="w-full py-6 bg-blue-600 text-white rounded-[2rem] font-black uppercase tracking-[4px] shadow-2xl shadow-blue-900/40 hover:bg-blue-700 transition-all active:scale-95">Générer le Certificat</button>
          ) : (
            <div className="animate-in zoom-in duration-500 space-y-6">
               <div className="p-8 bg-blue-50 rounded-[2.5rem] border-4 border-dashed border-blue-200 text-center relative group">
                 <p className="text-[10px] font-black text-blue-400 uppercase mb-4 tracking-widest">Clef de Licence Unique</p>
                 <code className="text-2xl font-black text-blue-900 tracking-wider block mb-4">{generated}</code>
                 <button onClick={() => { navigator.clipboard.writeText(generated); setCopied(true); setTimeout(()=>setCopied(false), 2000); }} className="mx-auto flex items-center gap-2 bg-white px-4 py-2 rounded-xl text-[10px] font-black text-blue-600 border shadow-sm uppercase tracking-widest">
                   {copied ? <Check size={14}/> : <Copy size={14}/>} {copied ? 'Copié' : 'Copier'}
                 </button>
               </div>
               <div className="flex gap-4">
                 <button onClick={() => setGenerated(null)} className="flex-1 p-5 font-black text-slate-400 uppercase text-xs tracking-widest">Réinitialiser</button>
                 <button onClick={saveToCompany} className="flex-2 bg-slate-900 text-white p-5 rounded-2xl font-black uppercase text-xs tracking-widest shadow-xl flex items-center justify-center gap-3">Appliquer au Client <ChevronRight size={18}/></button>
               </div>
            </div>
          )}
          <button onClick={onClose} className="w-full text-slate-300 font-bold uppercase text-[9px] tracking-widest pt-4">Annuler l'opération</button>
        </div>
      </div>
    </div>
  );
}

// --- LICENSE FACTORY ---

// --- MAIN APP ---

function App() {
  const [user, setUser] = useState(null);
  const isRoot = user?.email === 'root@vigilus.app';
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(isRoot ? 'companies' : 'dashboard');
  const [printingSite, setPrintingSite] = useState(null);
  const [printingMonthlyReport, setPrintingMonthlyReport] = useState(false);
  const [selectedPatrol, setSelectedPatrol] = useState(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [patrols, setPatrols] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [agents, setAgents] = useState([]);
  const [sites, setSites] = useState([]);
  const [appUsers, setAppUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [sosAlerts, setSosAlerts] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [currentCompany, setCurrentCompany] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showCompanyHub, setShowCompanyHub] = useState(null);
  const [showAddSite, setShowAddSite] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [showLicenseFactory, setShowLicenseFactory] = useState(null);

  const unsubscribes = useRef([]);

  const clearSubscriptions = () => { unsubscribes.current.forEach(u => { if (typeof u === 'function') u(); }); unsubscribes.current = []; };

  const logAction = async (action, details) => {
    if (!selectedCompanyId) return;
    try { await addDoc(collection(db, 'users', selectedCompanyId, 'audit_logs'), { action, details, userName: user?.email || 'Inconnu', timestamp: Date.now() }); } catch (e) {}
  };

  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, async (u) => {
      clearSubscriptions();
      if (u) {
        if (u.email === 'root@vigilus.app') {
          const unsub = onSnapshot(collection(db, 'users', u.uid, 'companies'), (s) => {
            const list = s.docs.map(d => ({ id: d.id, ...d.data() }));
            setCompanies(list);
            if (list.length > 0 && !selectedCompanyId) setSelectedCompanyId(list[0].id);
          });
          unsubscribes.current.push(unsub);
        } else {
          // Robust mapping for Company Admins and other users
          let companyId = null;

          // 1. Try by UID
          const userDoc = await getDoc(doc(db, 'accounts', u.uid));
          if (userDoc.exists()) {
             companyId = userDoc.data().companyId;
          } else {
             // 2. Try by email search in accounts
             const q = query(collection(db, 'accounts'), where('email', '==', u.email));
             const qSnap = await getDocs(q);
             if (!qSnap.empty) {
               const data = qSnap.docs[0].data();
               companyId = data.companyId;
               // Optional: Auto-link UID for future fast access
               await setDoc(doc(db, 'accounts', u.uid), data, { merge: true });
             }
          }

          setSelectedCompanyId(companyId || u.uid);
        }
        setUser(u);
      } else { setUser(null); }
      setLoading(false);
    });
    return () => { unsubAuth(); clearSubscriptions(); };
  }, []);

  useEffect(() => {
    if (selectedCompanyId) {
      subscribeToData(selectedCompanyId);
      if (isRoot && companies.length > 0) {
        const comp = companies.find(c => c.id === selectedCompanyId);
        if (comp) {
          setCurrentCompany(comp);
          document.documentElement.style.setProperty('--primary-color', comp.primaryColor || '#2563eb');
        }
      } else if (!isRoot && user) {
        const unsub = onSnapshot(doc(db, 'users', selectedCompanyId, 'company_profile', 'main'), (docSnap) => {
           if (docSnap.exists()) {
             const data = docSnap.data();
             setCurrentCompany(data);
             document.documentElement.style.setProperty('--primary-color', data.primaryColor || '#2563eb');
           }
        });
        unsubscribes.current.push(unsub);
      }
    }
  }, [selectedCompanyId, isRoot, companies, user]);

  const subscribeToData = (id) => {
    setPatrols([]); setIncidents([]); setAgents([]); setSites([]); setAppUsers([]); setSosAlerts([]); setAuditLogs([]);
    const q1 = onSnapshot(query(collection(db, 'users', id, 'finished_patrols'), orderBy('endTime', 'desc')), s => setPatrols(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    const q2 = onSnapshot(query(collection(db, 'users', id, 'standalone_incidents'), orderBy('timestamp', 'desc')), s => setIncidents(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    const q3 = onSnapshot(collection(db, 'users', id, 'agents'), s => setAgents(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    const q4 = onSnapshot(collection(db, 'users', id, 'sites'), s => setSites(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    const q5 = onSnapshot(collection(db, 'users', id, 'users'), s => setAppUsers(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    const q6 = onSnapshot(query(collection(db, 'users', id, 'sos_alerts'), orderBy('timestamp', 'desc')), s => {
      const alerts = s.docs.map(d => ({ id: d.id, ...d.data() }));
      setSosAlerts(alerts);
      if (alerts.length > 0 && alerts[0].status === 'ACTIVE') { try { new Audio('https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3').play(); } catch(e){} }
    });
    const q7 = onSnapshot(query(collection(db, 'users', id, 'audit_logs'), orderBy('timestamp', 'desc')), s => setAuditLogs(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    unsubscribes.current.push(q1, q2, q3, q4, q5, q6, q7);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    const loginEmail = email.includes('@') ? email : `${email}@vigilus.app`;
    try {
      await signInWithEmailAndPassword(auth, loginEmail, password);
    }
    catch (error) {
      console.error(error);
      alert(`Échec de connexion : ${error.message}`);
    }
  };

  if (loading) return <div className="h-screen flex items-center justify-center bg-slate-900"><Loader2 className="animate-spin text-blue-500 w-16 h-16" /></div>;

  const isExpired = currentCompany?.expiryDate && !isAfter(new Date(currentCompany.expiryDate), new Date());
  if (user && isExpired && !isRoot) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-955 font-sans p-6 text-center">
        <div className="bg-white p-16 rounded-[5rem] shadow-2xl max-w-2xl border-t-[12px] border-red-600 animate-in zoom-in duration-500">
           <div className="w-24 h-24 bg-red-100 text-red-600 rounded-[2.5rem] flex items-center justify-center mx-auto mb-10"><Lock size={48} /></div>
           <h2 className="text-4xl font-black text-slate-900 uppercase tracking-tighter mb-4 leading-none text-center">Accès Interrompu</h2>
           <p className="text-slate-400 font-bold uppercase text-[10px] tracking-[5px] mb-8 italic">Licence Expirée</p>
           <p className="text-slate-600 font-medium text-lg leading-relaxed mb-12">Votre abonnement VIGILUS PRO a expiré le <span className="font-black text-red-600">{format(new Date(currentCompany.expiryDate), 'dd/MM/yyyy')}</span>. Veuillez contacter votre gestionnaire ou le service commercial pour renouveler vos accès.</p>
           <button onClick={() => signOut(auth)} className="bg-slate-900 text-white px-12 py-5 rounded-2xl font-black uppercase text-xs tracking-[3px] shadow-xl hover:bg-black transition-all">Quitter la Console</button>
        </div>
      </div>
    );
  }

  const daysToExpiry = currentCompany?.expiryDate ? differenceInDays(new Date(currentCompany.expiryDate), new Date()) : 999;
  const showWarningBanner = user && !isRoot && daysToExpiry > 0 && daysToExpiry <= 30;

  if (!user) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 px-4 font-sans relative">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10 opacity-20"><div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-600 rounded-full blur-[120px]"></div><div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-indigo-600 rounded-full blur-[120px]"></div></div>
      <form onSubmit={handleLogin} className="bg-white/95 backdrop-blur-xl p-16 rounded-[4rem] shadow-[0_50px_100px_-20px_rgba(0,0,0,0.5)] w-full max-w-md animate-in fade-in zoom-in duration-700 border border-white/20">
        <div className="flex justify-center mb-10"><div className="bg-blue-600 p-6 rounded-[2rem] shadow-2xl animate-pulse"><Shield className="text-white" size={48} /></div></div>
        <h1 className="text-6xl font-black text-center text-slate-900 tracking-tighter mb-2 leading-none uppercase">Vigilus<br/><span className="text-blue-600 italic">PRO</span></h1>
        <p className="text-slate-400 text-center mb-16 font-black uppercase tracking-[8px] text-[10px]">Cloud Command Center</p>
        <div className="space-y-6">
          <input type="email" placeholder="Identifiant Cloud" required className="w-full p-6 bg-slate-50 border-2 border-transparent rounded-[1.75rem] outline-none focus:border-blue-600 font-black text-slate-800 transition-all shadow-inner" value={email} onChange={e => setEmail(e.target.value)} />
          <input type="password" placeholder="Clé de Session" required className="w-full p-6 bg-slate-50 border-2 border-transparent rounded-[1.75rem] outline-none focus:border-blue-600 font-black text-slate-800 transition-all shadow-inner" value={password} onChange={e => setPassword(e.target.value)} />
        </div>
        <button type="submit" className="w-full bg-blue-600 text-white p-7 rounded-[1.75rem] font-black mt-16 hover:bg-blue-700 transition-all shadow-2xl shadow-blue-900/40 active:scale-95 uppercase tracking-[5px] text-xs">Ouvrir la Console</button>
        <div className="mt-12 pt-10 border-t border-slate-100 flex flex-col items-center gap-4">
           <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Besoin de l'application mobile ?</p>
           <a href="https://vigilus.app/dl/vigilus-pro.apk" download className="flex items-center gap-3 bg-slate-900 text-white px-8 py-3.5 rounded-full font-black text-[10px] uppercase tracking-widest hover:bg-blue-600 transition-all shadow-lg active:scale-95"><Smartphone size={18}/> Télécharger APK</a>
        </div>
      </form>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#fcfcfc] flex text-slate-800 font-sans transition-all selection:bg-blue-100 selection:text-blue-900">
      <style>{`
        :root { --primary-color: #2563eb; }
        .bg-blue-600, .bg-blue-900 { background-color: var(--primary-color) !important; }
        .text-blue-600, .text-blue-900 { color: var(--primary-color) !important; }
        .border-blue-600 { border-color: var(--primary-color) !important; }
        .focus\\\\:border-blue-600:focus { border-color: var(--primary-color) !important; }
        .custom-scrollbar::-webkit-scrollbar { width: 5px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
      `}</style>

      <aside className="w-80 bg-slate-900 text-white flex flex-col sticky h-screen top-0 shrink-0 shadow-2xl z-[100] border-r border-white/5">
        <div className="p-10 flex items-center gap-5 bg-white/5 border-b border-white/5 mb-8">
          {currentCompany?.logoUrl ? <img src={currentCompany.logoUrl} alt="Logo" className="h-14 w-auto object-contain" /> : <div className="bg-blue-600 p-3 rounded-2xl shadow-2xl"><Shield size={32} className="text-white" /></div>}
          <div className="overflow-hidden">
            <span className="text-2xl font-black tracking-tighter uppercase block truncate leading-none">{currentCompany?.name || 'Vigilus'}</span>
            <span className="text-[10px] font-black text-blue-500 tracking-[4px] uppercase mt-2 block opacity-70">Security PRO</span>
          </div>
        </div>

        <div className="flex-1 px-8 space-y-10 overflow-y-auto custom-scrollbar pt-4 pb-12">
          {isRoot ? (
            <MenuSection title="Administration Master">
              <NavItem active={activeTab === 'companies'} icon={<Building2/>} label="Réseau Entreprises" onClick={() => setActiveTab('companies')} />
              <NavItem active={activeTab === 'factory'} icon={<Award/>} label="Générateur de Clés" onClick={() => setActiveTab('factory')} />
              <NavItem active={activeTab === 'maintenance'} icon={<Shield/>} label="Maintenance APK" onClick={() => setActiveTab('maintenance')} />
            </MenuSection>
          ) : (
             <>
               <MenuSection title="Supervision">
                 <NavItem active={activeTab === 'dashboard'} icon={<LayoutDashboard/>} label="Tableau de Bord" onClick={() => setActiveTab('dashboard')} />
                 <NavItem active={activeTab === 'map'} icon={<MapPinned/>} label="Cartographie GPS" onClick={() => setActiveTab('map')} />
                 <NavItem active={activeTab === 'patrols'} icon={<FileSearch/>} label="Archives de Rondes" onClick={() => setActiveTab('patrols')} />
               </MenuSection>

               <MenuSection title="Opérationnel">
                 <NavItem active={activeTab === 'agents'} icon={<ShieldCheck/>} label="Ressources Humaines" onClick={() => setActiveTab('agents')} />
                 <NavItem active={activeTab === 'sites'} icon={<Landmark/>} label="Parc Immobilier" onClick={() => setActiveTab('sites')} />
                 <NavItem active={activeTab === 'users'} icon={<KeyRound/>} label="Accès & Terminaux" onClick={() => setActiveTab('users')} />
               </MenuSection>
             </>
          )}

          <MenuSection title="Gouvernance">
            <NavItem active={activeTab === 'audit'} icon={<History/>} label="Historique Logs" onClick={() => setActiveTab('audit')} />
            <NavItem active={activeTab === 'settings'} icon={<Settings/>} label="Paramétrages" onClick={() => setActiveTab('settings')} />
          </MenuSection>

          <div className="pt-10 border-t border-white/5">
            <MenuSection title="Installateur">
              <a href="https://vigilus.app/dl/vigilus-pro.apk" download className="flex items-center gap-4 w-full p-4 rounded-2xl transition-all font-black text-xs uppercase tracking-widest text-slate-400 hover:bg-white/5 hover:text-white">
                <Smartphone size={18}/> <span>Télécharger APK</span>
              </a>
            </MenuSection>
          </div>
        </div>

        <div className="p-8 border-t border-white/5 bg-black/30">
          <div className="flex items-center gap-4 mb-8 bg-white/5 p-4 rounded-[1.5rem] border border-white/5">
             <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-indigo-800 rounded-2xl flex items-center justify-center font-black shadow-2xl">AD</div>
             <div className="overflow-hidden"><p className="text-sm font-black truncate tracking-tighter uppercase">{user.email.split('@')[0]}</p><p className="text-[10px] text-slate-500 font-black uppercase tracking-[3px] mt-1">Status: OK</p></div>
          </div>
          <button onClick={() => { clearSubscriptions(); signOut(auth); }} className="w-full p-5 bg-red-600/10 text-red-500 rounded-[1.5rem] font-black flex items-center justify-center gap-3 hover:bg-red-600 hover:text-white transition-all shadow-xl active:scale-95 uppercase text-[10px] tracking-[4px]">Se Déconnecter</button>
        </div>
      </aside>

      <main className="flex-1 flex flex-col h-screen overflow-hidden relative">
        {showWarningBanner && (
          <div className="bg-gradient-to-r from-orange-600 to-red-600 text-white px-10 py-3 flex justify-between items-center animate-in slide-in-from-top duration-700 z-[110] shadow-2xl">
            <div className="flex items-center gap-4 italic font-black text-xs uppercase tracking-[2px]">
              <BellRing size={20} className="animate-bounce" />
              <span>Alerte Renouvellement : Votre licence expire dans {daysToExpiry} jours.</span>
            </div>
            <button className="bg-white text-orange-600 px-6 py-2 rounded-full font-black text-[10px] uppercase tracking-widest shadow-xl hover:scale-105 transition-all">Renouveler Maintenant</button>
          </div>
        )}

        <header className="h-28 bg-white/90 backdrop-blur-xl border-b border-slate-100 px-12 flex items-center justify-between shrink-0 shadow-sm z-[90]">
           <div className="flex items-center gap-12">
             <h1 className="text-5xl font-black text-slate-900 tracking-tighter uppercase leading-none italic">{activeTab === 'dashboard' ? 'Dashboard' : activeTab}</h1>
             {isRoot && companies.length > 0 && <div className="flex items-center gap-5 bg-white shadow-[0_15px_35px_rgba(0,0,0,0.05)] p-3 px-6 rounded-full border-2 border-slate-50 group hover:border-blue-100 transition-all">
                 <Globe size={20} className="text-slate-400 group-hover:text-blue-600 transition-colors" />
                 <select className="bg-transparent border-none outline-none text-xs font-black text-blue-900 pr-10 py-1 cursor-pointer uppercase tracking-[4px]" value={selectedCompanyId || ''} onChange={e => setSelectedCompanyId(e.target.value)}>{companies.map(c => <option key={c.id} value={c.id}>{c.name.toUpperCase()}</option>)}</select>
               </div>}
           </div>
           <div className="flex items-center gap-12">
             <div className="hidden lg:flex flex-col text-right">
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-[6px] mb-1">SYSTÈME DÉPLOYÉ</p>
                <p className="text-sm font-black text-green-600 flex items-center justify-end gap-3 uppercase tracking-tighter"><span className="w-3 h-3 bg-green-500 rounded-full animate-pulse shadow-[0_0_20px_#22c55e]"></span> TEMPS RÉEL</p>
             </div>
           </div>
        </header>

        <div className="flex-1 overflow-y-auto p-12 custom-scrollbar bg-[#fcfcfc] relative">
          <div className="max-w-[1600px] mx-auto">
            {activeTab === 'companies' && <ManagementView title="Réseau Entreprises" items={companies} col="companies" icon={<Building2/>} isCompany={true} uid={user.uid} currentUser={user} companies={companies} onAction={logAction} onOpenCompanyHub={setShowCompanyHub} />}
            {activeTab === 'factory' && <KeyGeneratorView />}
            {activeTab === 'dashboard' && <DashboardView patrols={patrols} agents={agents} sites={sites} incidents={incidents} onNavigate={setActiveTab} />}
            {activeTab === 'map' && <MapView incidents={incidents} />}
            {activeTab === 'patrols' && <PatrolsArchivesView patrols={patrols} onViewDetails={setSelectedPatrol} uid={selectedCompanyId} onPrintMonthly={() => setPrintingMonthlyReport(true)} onAction={logAction} />}
            {activeTab === 'agents' && <ManagementView title="Personnel" items={agents} col="agents" icon={<ShieldCheck/>} uid={selectedCompanyId} currentUser={user} companies={companies} onAction={logAction} />}
            {activeTab === 'sites' && <SitesView sites={sites} patrols={patrols} incidents={incidents} onPrint={setPrintingSite} onEdit={(s) => { setEditingSite(s); setShowAddSite(true); }} onDelete={async (s) => { if(window.confirm("Supprimer ce site ?")) await deleteDoc(doc(db, 'users', selectedCompanyId, 'sites', s.id)); }} onAdd={() => { setEditingSite(null); setShowAddSite(true); }} />}
            {activeTab === 'users' && <ManagementView title="Utilisateurs" items={appUsers} col="users" icon={<KeyRound/>} isUser={true} uid={selectedCompanyId} currentUser={user} companies={companies} onAction={logAction} />}
            {activeTab === 'audit' && <AuditLogsView logs={auditLogs} />}
            {activeTab === 'settings' && <SettingsView uid={selectedCompanyId} currentUser={user} appUsers={appUsers} />}
            {activeTab === 'maintenance' && <MaintenanceView onAction={logAction} />}
          </div>
        </div>
      </main>

      {showAddSite && <AddSiteModal site={editingSite} uid={selectedCompanyId} onClose={() => setShowAddSite(false)} />}
      {showCompanyHub && <CompanyHubModal company={showCompanyHub} uid={user.uid} onClose={() => setShowCompanyHub(null)} onAction={logAction} companies={companies} />}

      {sosAlerts.filter(a => a.status === 'ACTIVE').map(alert => (
        <div key={alert.id} className="fixed bottom-12 right-12 z-[1000] bg-red-600 text-white p-12 rounded-[4rem] shadow-[0_50px_100px_-20px_rgba(220,38,38,0.5)] animate-bounce flex items-center gap-10 border-8 border-white/20 backdrop-blur-3xl">
          <div className="bg-white p-6 rounded-[2.5rem] text-red-600 shadow-2xl scale-125"><Volume2 className="animate-pulse" size={56} /></div>
          <div><h4 className="text-4xl font-black uppercase tracking-tighter leading-none mb-3">DANGER IMMÉDIAT</h4><p className="text-xl font-bold opacity-90 uppercase tracking-[4px]">{alert.agentName || 'Agent Inconnu'}</p>
          <div className="flex gap-4 mt-10"><button onClick={async () => { await setDoc(doc(db, 'users', selectedCompanyId, 'sos_alerts', alert.id), { status: 'RESOLVED', resolvedAt: Date.now(), resolvedBy: user.email }, { merge: true }); logAction('SOS_RESOLVE', `Urgence ${alert.agentName} traitée.`); }} className="bg-white text-red-600 px-12 py-5 rounded-3xl font-black uppercase text-xs tracking-[4px] shadow-2xl transition-all hover:scale-110 active:scale-95">Acquitter l'Alerte</button></div></div>
        </div>
      ))}

      {printingSite && <PrintView site={printingSite} onClear={() => setPrintingSite(null)} />}
      {printingMonthlyReport && <MonthlyReportView patrols={patrols} incidents={incidents} company={currentCompany} onClear={() => setPrintingMonthlyReport(false)} />}
      {selectedPatrol && <PatrolDetailView patrol={selectedPatrol} onClose={() => setSelectedPatrol(null)} />}
      {showLicenseFactory && <LicenseFactory company={showLicenseFactory} onClose={() => setShowLicenseFactory(null)} />}
    </div>
  );
}

export default App;
